import React, { Fragment, useMemo, useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./KYC-Dashboard.css";
import {
	imgFilter,
	KYCAccountNameImage,
	KYCContactsImage,
	KYCCylindersImage,
	KYCDesignImage,
	KYCGroupCompanyImage,
	KYCMaterialsImage,
} from "../Elite/imageAssets";
import { Breadcrumb, Spin, Input, Card, Tabs, Select, DatePicker, Button } from "antd";
import {
	EnvironmentFilled,
	EnvironmentOutlined,
	MinusOutlined,
	PlusOutlined,
	UnorderedListOutlined,
} from "@ant-design/icons";
import { GoogleMap, InfoWindow, Marker, DirectionsRenderer, useJsApiLoader } from "@react-google-maps/api";
import addLocationIcon from "../../assets/images/add-location.svg";
import blackTickIcon from "../../assets/images/black-tick.svg";
import blueTickIcon from "../../assets/images/blue-tick.svg";
import infoWindowChevronIcon from "../../assets/images/infowindow-chevron-double-right.svg";
import infoWindowLocationIcon from "../../assets/images/infowindow-location.svg";
import infoWindowLocationRedIcon from "../../assets/images/infowindow-location-red.svg";
import infoWindowMailIcon from "../../assets/images/infowindow-mail.svg";
import infoWindowPhoneIcon from "../../assets/images/infowindow-phone.svg";
import locationFromIcon from "../../assets/images/location-from.svg";
import locationSwapIcon from "../../assets/images/location-swap.svg";
import locationToIcon from "../../assets/images/location-to.svg";
import knackLogo from "../../assets/images/knackLogo.png";
import redTickIcon from "../../assets/images/red-tick.svg";
import selectFromLocationIcon from "../../assets/images/select-from-location.svg";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";
import KycGroupCompanyTab from "./KycGroupCompanyTab";
import KycAccountTab from "./KycAccountTab";
import KycMaterialsTab from "./KycMaterialsTab";
import KycQuotationTab from "./KycQuotationTab";
import KycOrdersTab from "./KycOrdersTab";
import KycDesignTab from "./KycDesignTab";
import KycMapPlaceInput from "./KycMapPlaceInput";
import { buildLocationFilter, extractLocation, filterMarkersNearRoute, getDirectionsPathCoordinates, getPlaceGeometryLocation } from "./kycMapRouteUtils";
import useKycMapCompanyCluster from "./useKycMapCompanyCluster";
import {
	buildKycCompanyTabUrl,
	buildKycDashboardReturnLink,
	buildKycTableSearchUrl,
	buildKycTileCountsPayloadWithScope,
	getMaterialTileCounts,
	getDesignTileCounts,
	getCylinderTileCounts,
	getGroupCompanyId,
	getGroupCompanyName,
	getGroupCompanyRowLabel,
	KYC_GROUP_COMPANY_PARAM,
	KYC_MATERIALS_CUSTOMER_PARAM,
	KYC_COMPANY_TAB_LABELS,
	KYC_TABLE_SEARCH_PARAM,
	recordMatchesGroupCompanyScope,
} from "./kycHelpers";
import moment from "moment";

const { Search } = Input;
const { TabPane } = Tabs;
const { Option } = Select;

const defaultPayload = {
	fromDate: "2026-04-09T12:10:21.201Z",
	toDate: "2026-04-09T12:10:21.201Z",
	distributionChannel: "",
	priceGroup: "",
	salesDistrict: "",
	salesOffice: "",
	country: "",
	director: "",
	generalManager: "",
	continentalManager: "",
	regionalManager: "",
	areaManager: "",
	customerGroup: "",
	materialGroup: "",
	materialType: "",
	materialDivision: "",
	materialStructure: "",
	materialCharacteristics: "",
	materialHierarchy1: "",
	materialHierarchy2: "",
	materialHierarchy3: "",
};

const getFirstAvailableValue = (...values) =>
	values.find((value) => value !== undefined && value !== null && value !== "");
const defaultFilterValues = {
	fromDate: null,
	toDate: null,
	distributionChannel: [],
	priceGroup: [],
	salesDistrict: [],
	salesOffice: [],
	country: [],
	director: [],
	generalManager: [],
	continentalManager: [],
	regionalManager: [],
	areaManager: [],
	customerGroup: [],
	materialGroup: [],
	materialType: [],
	materialDivision: [],
	materialStructure: [],
	materialCharacteristics: [],
	materialHierarchy1: [],
	materialHierarchy2: [],
	materialHierarchy3: [],
};

const formatMultiSelectPayloadValue = (value) => {
	if (Array.isArray(value)) {
		return value.length ? value.join(",") : "";
	}
	return value || "";
};
const getFilterOptionList = (responseData, keys) => {
	for (const key of keys) {
		const value = responseData?.[key];
		if (Array.isArray(value)) {
			return value;
		}
	}

	return [];
};
const normalizeFilterOptions = (options = []) =>
	options
		.map((item, index) => {
			if (typeof item === "string" || typeof item === "number") {
				return { label: item, value: item, key: `${item}-${index}` };
			}

			if (!item || typeof item !== "object") {
				return null;
			}

			const value =
				item.value ??
				item.id ??
				item.sapCode ??
				item.code ??
				item.name ??
				item.label ??
				item.text ??
				item.title ??
				item.key;
			const label =
				item.label ??
				item.name ??
				item.text ??
				item.title ??
				item.value ??
				item.code ??
				item.sapCode ??
				item.id;

			if (value === undefined || label === undefined) {
				return null;
			}

			return {
				label,
				value,
				key: `${value}-${index}`,
			};
		})
		.filter(Boolean);
const getAccountRowCompanyName = (record = {}) => {
	for (const key of ["accountName", "companyName", "ispl_AccountName", "ispl_accountName"]) {
		const v = record[key];
		if (v !== undefined && v !== null && String(v).trim() !== "") {
			return String(v).trim();
		}
	}
	return "";
};

const VALID_COMPANY_NAV_TAB_KEYS = new Set([
	"groupCompany",
	"account",
	"materials",
	"quotation",
	"orders",
	"design",
]);

const readCompanyNavTabFromWindow = () => {
	if (typeof window === "undefined") {
		return "groupCompany";
	}
	const tab = new URLSearchParams(window.location.search).get("companyTab");
	return tab && VALID_COMPANY_NAV_TAB_KEYS.has(tab) ? tab : "groupCompany";
};

const readKycViewModeFromWindow = () => {
	if (typeof window === "undefined") {
		return "list";
	}
	return window.location.pathname === "/kyc/map" ? "map" : "list";
};

const KycDashboard = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isCardsLoading, setIsCardsLoading] = useState(false);
	const [activeCompanyTab, setActiveCompanyTab] = useState(readCompanyNavTabFromWindow);
	const [selectedGroupCompanyId, setSelectedGroupCompanyId] = useState("");
	const [selectedGroupCompanyName, setSelectedGroupCompanyName] = useState("");

	useEffect(() => {
		const tab = searchParams.get("companyTab");
		const resolved = tab && VALID_COMPANY_NAV_TAB_KEYS.has(tab) ? tab : "groupCompany";
		setActiveCompanyTab((prev) => (resolved !== prev ? resolved : prev));
	}, [searchParams]);

	useEffect(() => {
		const nextMode = location.pathname === "/kyc/map" ? "map" : "list";
		setKycViewMode((prev) => (prev !== nextMode ? nextMode : prev));
	}, [location.pathname]);

	const [groupCompanyTable, setGroupCompanyTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [accountTable, setAccountTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [materialsTable, setMaterialsTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [quotationTable, setQuotationTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [ordersTable, setOrdersTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [designTable, setDesignTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [isGroupCompanyLoading, setIsGroupCompanyLoading] = useState(false);
	const [isAccountLoading, setIsAccountLoading] = useState(false);
	const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
	const [isQuotationLoading, setIsQuotationLoading] = useState(false);
	const [isOrdersLoading, setIsOrdersLoading] = useState(false);
	const [isDesignLoading, setIsDesignLoading] = useState(false);
	const [isFiltersLoading, setIsFiltersLoading] = useState(false);
	const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
	const [filterPanelHeight, setFilterPanelHeight] = useState(0);
	const [tableSearchValue, setTableSearchValue] = useState(() => {
		const initialSearch = searchParams.get(KYC_TABLE_SEARCH_PARAM);
		return initialSearch && String(initialSearch).trim() ? String(initialSearch).trim() : "";
	});
	const kycSearchFromUrl = searchParams.get(KYC_TABLE_SEARCH_PARAM) || "";
	const materialsCustomerFromUrl = searchParams.get(KYC_MATERIALS_CUSTOMER_PARAM) || "";
	const kycGroupCompanyFromUrl = searchParams.get(KYC_GROUP_COMPANY_PARAM) || "";
	const companyTabFromUrl = searchParams.get("companyTab") || "";
	const materialSearchFromUrl = companyTabFromUrl === "materials" ? kycSearchFromUrl : "";

	const resolveGroupCompanyNameFromNavigation = () => {
		const fromState = location.state?.groupCompanyName;
		if (fromState && String(fromState).trim()) {
			return String(fromState).trim();
		}
		if (kycGroupCompanyFromUrl.trim()) {
			return kycGroupCompanyFromUrl.trim();
		}
		return "";
	};

	useEffect(() => {
		const hasSearch = Boolean(kycSearchFromUrl.trim());
		const hasMaterialSearch = Boolean(materialSearchFromUrl.trim());
		const hasMaterialsCustomer = Boolean(materialsCustomerFromUrl.trim());
		const hasGroupCompanyInUrl = Boolean(kycGroupCompanyFromUrl.trim());

		if (hasSearch || hasMaterialsCustomer || hasGroupCompanyInUrl) {
			if (companyTabFromUrl === "materials") {
				setActiveCompanyTab("materials");
				setMaterialsTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));

				const groupIdFromState = location.state?.groupCompanyId;
				const groupNameFromState = location.state?.groupCompanyName;
				const accountFromState = location.state?.accountName;

				if (hasMaterialsCustomer) {
					const customer = materialsCustomerFromUrl.trim();
					setMaterialsIsplCustomer(customer);
					if (accountFromState && String(accountFromState).trim()) {
						setSelectedAccountName(String(accountFromState).trim());
					} else if (
						groupIdFromState ||
						groupNameFromState ||
						hasSearch
					) {
						setSelectedAccountName(customer);
					} else {
						setSelectedAccountName("");
					}
				} else {
					setMaterialsIsplCustomer("");
					setSelectedAccountName("");
				}

				if (groupIdFromState && String(groupIdFromState).trim()) {
					setSelectedGroupCompanyId(String(groupIdFromState).trim());
				} else {
					setSelectedGroupCompanyId("");
				}
				if (groupNameFromState && String(groupNameFromState).trim()) {
					setSelectedGroupCompanyName(String(groupNameFromState).trim());
				} else {
					setSelectedGroupCompanyName("");
				}
				setTableSearchValue(hasMaterialSearch ? materialSearchFromUrl.trim() : "");
			} else {
				setTableSearchValue(kycSearchFromUrl.trim());

				if (companyTabFromUrl === "account") {
					const groupIdFromState = location.state?.groupCompanyId;
					const groupNameFromNavigation = resolveGroupCompanyNameFromNavigation();
					const accountFromState = location.state?.accountName;

					if (groupIdFromState && String(groupIdFromState).trim()) {
						setSelectedGroupCompanyId(String(groupIdFromState).trim());
					}
					if (groupNameFromNavigation) {
						setSelectedGroupCompanyName(groupNameFromNavigation);
					}
					if (accountFromState && String(accountFromState).trim()) {
						setSelectedAccountName(String(accountFromState).trim());
					} else if (hasSearch) {
						setSelectedAccountName(kycSearchFromUrl.trim());
					} else {
						setSelectedAccountName("");
					}
					setMaterialsIsplCustomer("");
					setActiveCompanyTab("account");
					setAccountTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
				} else {
					setSelectedGroupCompanyId("");
					setSelectedGroupCompanyName("");
					setSelectedAccountName("");
				}

				if (companyTabFromUrl === "account") {
					// handled above
				} else if (companyTabFromUrl === "design") {
					setActiveCompanyTab("design");
					setDesignTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
				} else if (hasMaterialsCustomer) {
					setActiveCompanyTab("materials");
					setMaterialsIsplCustomer(materialsCustomerFromUrl.trim());
					setTableSearchValue("");
					setMaterialsTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
				} else {
					setActiveCompanyTab("groupCompany");
					setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
				}
			}

			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.delete(KYC_TABLE_SEARCH_PARAM);
					next.delete(KYC_MATERIALS_CUSTOMER_PARAM);
					next.delete(KYC_GROUP_COMPANY_PARAM);
					return next;
				},
				{ replace: true }
			);
			return;
		}

		const tableSearch = location.state?.tableSearch;
		if (tableSearch && String(tableSearch).trim()) {
			setTableSearchValue(String(tableSearch).trim());
			setSelectedGroupCompanyId("");
			setSelectedGroupCompanyName("");
			setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		const materialsCustomerFromState = location.state?.materialsCustomer;
		const hasMaterialsCustomerState =
			materialsCustomerFromState && String(materialsCustomerFromState).trim();

		if (hasMaterialsCustomerState) {
			const customer = String(materialsCustomerFromState).trim();
			setActiveCompanyTab("materials");
			setMaterialsIsplCustomer(customer);
			setTableSearchValue("");
			setMaterialsTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));

			const groupCompanyId = location.state?.groupCompanyId;
			const groupCompanyName = location.state?.groupCompanyName;
			if (groupCompanyId && String(groupCompanyId).trim()) {
				setSelectedGroupCompanyId(String(groupCompanyId).trim());
			}
			if (groupCompanyName && String(groupCompanyName).trim()) {
				setSelectedGroupCompanyName(String(groupCompanyName).trim());
			}
			const accountNameFromState = location.state?.accountName;
			if (accountNameFromState && String(accountNameFromState).trim()) {
				setSelectedAccountName(String(accountNameFromState).trim());
			} else if (hasMaterialsCustomerState && (groupCompanyId || groupCompanyName)) {
				setSelectedAccountName(customer);
			}
			return;
		}

		const returnTabFromState = location.state?.returnTab;
		const accountNameFromState = location.state?.accountName;
		const designMasterFromState = location.state?.designMasterNumber;
		const crmOrderNumberFromState = location.state?.crmOrderNumber;
		const materialNameFromState = location.state?.materialName;

		if (returnTabFromState === "materials") {
			const materialSearch =
				(materialNameFromState && String(materialNameFromState).trim()) ||
				(location.state?.tableSearch && String(location.state.tableSearch).trim()) ||
				"";
			setActiveCompanyTab("materials");
			if (materialSearch) {
				setTableSearchValue(materialSearch);
			}
			setMaterialsTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		if (returnTabFromState === "design") {
			const designSearch =
				(designMasterFromState && String(designMasterFromState).trim()) ||
				(location.state?.tableSearch && String(location.state.tableSearch).trim()) ||
				"";

			setActiveCompanyTab("design");
			setMaterialsIsplCustomer("");
			setSelectedGroupCompanyId("");
			setSelectedGroupCompanyName("");
			setSelectedAccountName("");
			if (designSearch) {
				setTableSearchValue(designSearch);
			}
			setDesignTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		if (returnTabFromState === "orders") {
			const orderSearch =
				(crmOrderNumberFromState && String(crmOrderNumberFromState).trim()) ||
				(location.state?.tableSearch && String(location.state.tableSearch).trim()) ||
				"";

			setActiveCompanyTab("orders");
			setMaterialsIsplCustomer("");
			setSelectedGroupCompanyId("");
			setSelectedGroupCompanyName("");
			setSelectedAccountName("");
			if (orderSearch) {
				setTableSearchValue(orderSearch);
			}
			setOrdersTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		const isAccountReturn =
			returnTabFromState === "account" ||
			(accountNameFromState && String(accountNameFromState).trim());

		if (isAccountReturn) {
			const accountName = accountNameFromState ? String(accountNameFromState).trim() : "";
			const groupId = location.state?.groupCompanyId;
			const groupName = resolveGroupCompanyNameFromNavigation();

			setActiveCompanyTab("account");
			setMaterialsIsplCustomer("");
			if (accountName) {
				setSelectedAccountName(accountName);
				setTableSearchValue(accountName);
			}
			if (groupId && String(groupId).trim()) {
				setSelectedGroupCompanyId(String(groupId).trim());
			}
			if (groupName) {
				setSelectedGroupCompanyName(groupName);
			}
			setAccountTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		const groupCompanyId = location.state?.groupCompanyId;
		const groupCompanyName = location.state?.groupCompanyName;
		const hasId = groupCompanyId && String(groupCompanyId).trim();
		const hasName = groupCompanyName && String(groupCompanyName).trim();

		if (!hasId && !hasName) {
			return;
		}

		setActiveCompanyTab("groupCompany");
		setMaterialsIsplCustomer("");
		setSelectedAccountName("");
		setTableSearchValue("");
		if (hasId) {
			setSelectedGroupCompanyId(String(groupCompanyId).trim());
		}
		if (hasName) {
			setSelectedGroupCompanyName(String(groupCompanyName).trim());
		}
		setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
	}, [
		kycSearchFromUrl,
		materialsCustomerFromUrl,
		kycGroupCompanyFromUrl,
		companyTabFromUrl,
		location.state?.tableSearch,
		location.state?.materialsCustomer,
		location.state?.accountName,
		location.state?.returnTab,
		location.state?.designMasterNumber,
		location.state?.groupCompanyId,
		location.state?.groupCompanyName,
		setSearchParams,
	]);

	const [kycViewMode, setKycViewMode] = useState(readKycViewModeFromWindow);
	const filterPanelContentRef = useRef(null);
	const [filterValues, setFilterValues] = useState(defaultFilterValues);
	const [filterOptions, setFilterOptions] = useState({
		distributionChannel: [],
		priceGroup: [],
		salesDistrict: [],
		salesOffice: [],
		country: [],
		director: [],
		generalManager: [],
		continentalManager: [],
		regionalManager: [],
		areaManager: [],
		customerGroup: [],
		materialGroup: [],
		materialType: [],
		materialDivision: [],
		materialStructure: [],
		materialCharacteristics: [],
		materialHierarchy1: [],
		materialHierarchy2: [],
		materialHierarchy3: [],
	});
	const [groupCompanySorter, setGroupCompanySorter] = useState({
		field: "CreatedOn",
		order: "DESC",
	});
	const [accountSorter, setAccountSorter] = useState({
		field: "CreatedOn",
		order: "DESC",
	});
	const [materialsSorter, setMaterialsSorter] = useState({
		field: "createdon",
		order: "DESC",
	});
	const [quotationSorter, setQuotationSorter] = useState({
		field: "createdOn",
		order: "DESC",
	});
	const [ordersSorter, setOrdersSorter] = useState({
		field: "createdOn",
		order: "DESC",
	});
	const [designSorter, setDesignSorter] = useState({
		field: "designMasterDate",
		order: "DESC",
	});
	const [materialsIsplCustomer, setMaterialsIsplCustomer] = useState("");
	const [selectedAccountName, setSelectedAccountName] = useState("");

	// --- Individual card count states (mirrors O2W pattern) ---
	const [groupCompanyCount, setGroupCompanyCount] = useState({});
	const [accountNameCount, setAccountNameCount] = useState({});
	const [contactsCount, setContactsCount] = useState({});
	const [materialsCount, setMaterialsCount] = useState({});
	const [designsCount, setDesignsCount] = useState({});
	const [cylindersCount, setCylindersCount] = useState({});

	const getCardTitle = (countObj, label, keys = ["total"]) => {
		const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
		return countValue !== undefined ? `${countValue} ${label}` : label;
	};

	const getCardCountValue = (countObj, keys) => {
		if (!countObj || Object.keys(countObj).length === 0) {
			return "-";
		}

		const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
		return countValue !== undefined ? countValue : "-";
	};

	const buildKycFilterPayload = () => ({
		...defaultPayload,
		fromDate: filterValues.fromDate ? moment(filterValues.fromDate).toISOString() : defaultPayload.fromDate,
		toDate: filterValues.toDate ? moment(filterValues.toDate).toISOString() : defaultPayload.toDate,
		distributionChannel: formatMultiSelectPayloadValue(filterValues.distributionChannel),
		priceGroup: formatMultiSelectPayloadValue(filterValues.priceGroup),
		salesDistrict: formatMultiSelectPayloadValue(filterValues.salesDistrict),
		salesOffice: formatMultiSelectPayloadValue(filterValues.salesOffice),
		country: formatMultiSelectPayloadValue(filterValues.country),
		director: formatMultiSelectPayloadValue(filterValues.director),
		generalManager: formatMultiSelectPayloadValue(filterValues.generalManager),
		continentalManager: formatMultiSelectPayloadValue(filterValues.continentalManager),
		regionalManager: formatMultiSelectPayloadValue(filterValues.regionalManager),
		areaManager: formatMultiSelectPayloadValue(filterValues.areaManager),
		customerGroup: formatMultiSelectPayloadValue(filterValues.customerGroup),
		materialGroup: formatMultiSelectPayloadValue(filterValues.materialGroup),
		materialType: formatMultiSelectPayloadValue(filterValues.materialType),
		materialDivision: formatMultiSelectPayloadValue(filterValues.materialDivision),
		materialStructure: formatMultiSelectPayloadValue(filterValues.materialStructure),
		materialCharacteristics: formatMultiSelectPayloadValue(filterValues.materialCharacteristics),
		materialHierarchy1: formatMultiSelectPayloadValue(filterValues.materialHierarchy1),
		materialHierarchy2: formatMultiSelectPayloadValue(filterValues.materialHierarchy2),
		materialHierarchy3: formatMultiSelectPayloadValue(filterValues.materialHierarchy3),
	});

	function buildKycTileCountsPayload() {
		return buildKycTileCountsPayloadWithScope(buildKycFilterPayload(), {
			groupCompanyId: selectedGroupCompanyId,
			isplCustomer: materialsIsplCustomer,
		});
	}

	const filterPayloadSignature = JSON.stringify(buildKycFilterPayload());

	useEffect(() => {
		handleGetKycGlobalFilters();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		handleGetKycDashboardCount();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterPayloadSignature, selectedGroupCompanyId, materialsIsplCustomer]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (activeCompanyTab === "groupCompany") {
				handleGetGroupCompanyList();
			}
		}, 500);

		return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		activeCompanyTab,
		filterPayloadSignature,
		tableSearchValue,
		groupCompanySorter.field,
		groupCompanySorter.order,
		groupCompanyTable.currentPage,
		selectedGroupCompanyId,
		selectedGroupCompanyName,
	]);

	useEffect(() => {
		if (activeCompanyTab !== "account") {
			return undefined;
		}

		const timeout = setTimeout(() => {
			handleGetAccountList();
		}, 500);

		return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		activeCompanyTab,
		selectedGroupCompanyId,
		filterPayloadSignature,
		tableSearchValue,
		accountSorter.field,
		accountSorter.order,
		accountTable.currentPage,
	]);

	useEffect(() => {
		if (activeCompanyTab !== "materials") {
			return undefined;
		}

		const timeout = setTimeout(() => {
			handleGetMaterialsList();
		}, 500);

		return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		activeCompanyTab,
		filterPayloadSignature,
		tableSearchValue,
		materialsSorter.field,
		materialsSorter.order,
		materialsTable.currentPage,
		materialsIsplCustomer,
	]);

	useEffect(() => {
		if (activeCompanyTab !== "quotation") {
			return undefined;
		}

		const timeout = setTimeout(() => {
			handleGetQuotationList();
		}, 500);

		return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		activeCompanyTab,
		filterPayloadSignature,
		tableSearchValue,
		quotationSorter.field,
		quotationSorter.order,
		quotationTable.currentPage,
		selectedGroupCompanyId,
	]);

	useEffect(() => {
		if (activeCompanyTab !== "orders") {
			return undefined;
		}

		const timeout = setTimeout(() => {
			handleGetOrdersList();
		}, 500);

		return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		activeCompanyTab,
		filterPayloadSignature,
		tableSearchValue,
		ordersSorter.field,
		ordersSorter.order,
		ordersTable.currentPage,
		selectedGroupCompanyId,
	]);

	useEffect(() => {
		if (activeCompanyTab !== "design") {
			return undefined;
		}

		const timeout = setTimeout(() => {
			handleGetDesignList();
		}, 500);

		return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		activeCompanyTab,
		filterPayloadSignature,
		tableSearchValue,
		designSorter.field,
		designSorter.order,
		designTable.currentPage,
		selectedGroupCompanyId,
	]);

	useLayoutEffect(() => {
		const contentNode = filterPanelContentRef.current;
		if (!contentNode) {
			return undefined;
		}

		const updateHeight = () => {
			setFilterPanelHeight(contentNode.scrollHeight);
		};
		const animationFrame = window.requestAnimationFrame(updateHeight);

		updateHeight();

		if (typeof ResizeObserver !== "undefined") {
			const resizeObserver = new ResizeObserver(() => {
				updateHeight();
			});
			resizeObserver.observe(contentNode);
			return () => {
				window.cancelAnimationFrame(animationFrame);
				resizeObserver.disconnect();
			};
		}

		window.addEventListener("resize", updateHeight);
		return () => {
			window.cancelAnimationFrame(animationFrame);
			window.removeEventListener("resize", updateHeight);
		};
	}, [isFiltersExpanded, isFiltersLoading, filterOptions, kycViewMode]);

	function buildGroupCompanyListPayload() {
		const payload = {
			pageindex: groupCompanyTable.currentPage,
			pagesize: groupCompanyTable.pageSize,
			sortcolumn: groupCompanySorter.field,
			orderBy: groupCompanySorter.order,
			search: tableSearchValue ? tableSearchValue.trim() : "",
			...buildKycFilterPayload(),
		};

		if (selectedGroupCompanyId) {
			payload.groupcomanyId = selectedGroupCompanyId;
		}

		if (selectedGroupCompanyName && !payload.search) {
			payload.search = selectedGroupCompanyName.trim();
		}

		return payload;
	}

	function buildAccountListPayload() {
		const payload = {
			pageindex: accountTable.currentPage,
			pagesize: accountTable.pageSize,
			sortcolumn: accountSorter.field,
			orderBy: accountSorter.order,
			search: tableSearchValue ? tableSearchValue.trim() : "",
			...buildKycFilterPayload(),
		};

		if (selectedGroupCompanyId) {
			payload.groupcomanyId = selectedGroupCompanyId;
		}

		return payload;
	}

	function buildMaterialListPayload() {
		return {
			pageindex: materialsTable.currentPage,
			pagesize: materialsTable.pageSize,
			sortcolumn: materialsSorter.field,
			orderBy: materialsSorter.order,
			search: tableSearchValue ? tableSearchValue.trim() : "",
			...buildKycFilterPayload(),
			ispl_customer: materialsIsplCustomer ? materialsIsplCustomer.trim() : "",
		};
	}

	function buildDesignListPayload() {
		const payload = {
			pageindex: designTable.currentPage,
			pagesize: designTable.pageSize,
			sortcolumn: designSorter.field,
			orderBy: designSorter.order,
			search: tableSearchValue ? tableSearchValue.trim() : "",
			...buildKycFilterPayload(),
			materialName: "",
		};

		if (selectedGroupCompanyId) {
			payload.groupcomanyId = selectedGroupCompanyId;
		}

		return payload;
	}

	function buildQuotationListPayload() {
		const payload = {
			pageindex: quotationTable.currentPage,
			pagesize: quotationTable.pageSize,
			sortcolumn: quotationSorter.field,
			orderBy: quotationSorter.order,
			search: tableSearchValue ? tableSearchValue.trim() : "",
			...buildKycFilterPayload(),
		};

		if (selectedGroupCompanyId) {
			payload.groupcomanyId = selectedGroupCompanyId;
		}

		return payload;
	}

	function buildOrdersListPayload() {
		const payload = {
			pageindex: ordersTable.currentPage,
			pagesize: ordersTable.pageSize,
			sortcolumn: ordersSorter.field,
			orderBy: ordersSorter.order,
			search: tableSearchValue ? tableSearchValue.trim() : "",
			...buildKycFilterPayload(),
		};

		if (selectedGroupCompanyId) {
			payload.groupcomanyId = selectedGroupCompanyId;
		}

		return payload;
	}

	function handleGetKycGlobalFilters() {
		setIsFiltersLoading(true);
		KycService.getKycGlobalFiltersAPI()
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data?.responseData || response.data || {};
					setFilterOptions({
						distributionChannel: normalizeFilterOptions(
							getFilterOptionList(responseData, ["distributionChannels", "distributionChannel", "distChannels"])
						),
						priceGroup: normalizeFilterOptions(
							getFilterOptionList(responseData, ["priceGroups", "priceGroup"])
						),
						salesDistrict: normalizeFilterOptions(
							getFilterOptionList(responseData, ["salesDistricts", "salesDistrict"])
						),
						salesOffice: normalizeFilterOptions(
							getFilterOptionList(responseData, ["salesOffices", "salesOffice"])
						),
						country: normalizeFilterOptions(getFilterOptionList(responseData, ["countries", "country"])),
						director: normalizeFilterOptions(getFilterOptionList(responseData, ["directors", "director"])),
						generalManager: normalizeFilterOptions(
							getFilterOptionList(responseData, ["generalManagers", "generalManager"])
						),
						continentalManager: normalizeFilterOptions(
							getFilterOptionList(responseData, ["continentalManagers", "continentalManager"])
						),
						regionalManager: normalizeFilterOptions(
							getFilterOptionList(responseData, ["regionalManagers", "regionalManager"])
						),
						areaManager: normalizeFilterOptions(getFilterOptionList(responseData, ["areaManagers", "areaManager"])),
						customerGroup: normalizeFilterOptions(
							getFilterOptionList(responseData, ["customerGroups", "customerGroup"])
						),
						materialGroup: normalizeFilterOptions(
							getFilterOptionList(responseData, ["materialGroups", "materialGroup"])
						),
						materialType: normalizeFilterOptions(
							getFilterOptionList(responseData, ["materialTypes", "materialType"])
						),
						materialDivision: normalizeFilterOptions(
							getFilterOptionList(responseData, ["materialDivisions", "materialDivision"])
						),
						materialStructure: normalizeFilterOptions(
							getFilterOptionList(responseData, ["materialStructures", "materialStructure"])
						),
						materialCharacteristics: normalizeFilterOptions(
							getFilterOptionList(responseData, ["materialCharacteristics", "materialCharacteristic"])
						),
						materialHierarchy1: normalizeFilterOptions(
							getFilterOptionList(responseData, ["materialHierarchy1", "materialHierarchies1"])
						),
						materialHierarchy2: normalizeFilterOptions(
							getFilterOptionList(responseData, ["materialHierarchy2", "materialHierarchies2"])
						),
						materialHierarchy3: normalizeFilterOptions(
							getFilterOptionList(responseData, ["materialHierarchy3", "materialHierarchies3"])
						),
					});
				} else {
					NotificationManager.error(response.data?.message || "Unable to load KYC global filters.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load KYC global filters.");
			})
			.finally(() => {
				setIsFiltersLoading(false);
			});
	}

	function handleGetGroupCompanyList() {
		setIsGroupCompanyLoading(true);
		const requestData = buildGroupCompanyListPayload();
		KycService.getKycGroupCompanyListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data.responseData || {};
					const groupCompanyList = Array.isArray(responseData)
						? responseData
						: responseData.kycGroupCompanyLists ||
						responseData.groupCompanyListing ||
						responseData.groupCompanyList ||
						responseData.data ||
						[];
					const scopedRows =
						selectedGroupCompanyId || selectedGroupCompanyName
							? groupCompanyList.filter((record) =>
								recordMatchesGroupCompanyScope(record, {
									groupCompanyId: selectedGroupCompanyId,
									groupCompanyName: selectedGroupCompanyName,
								})
							)
							: groupCompanyList;
					const totalRecords =
						selectedGroupCompanyId || selectedGroupCompanyName
							? scopedRows.length
							: Number(responseData.totalRecords || groupCompanyList.length || 0);
					const currentPage = Number(responseData.currentPage || groupCompanyTable.currentPage);

					setGroupCompanyTable((prev) => ({
						...prev,
						rows: scopedRows,
						totalRecords,
						currentPage,
					}));
				} else {
					NotificationManager.error(response.data?.message || "Unable to load Group Company listing.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load Group Company listing.");
			})
			.finally(() => {
				setIsGroupCompanyLoading(false);
			});
	}

	function handleGetAccountList() {
		setIsAccountLoading(true);
		const requestData = buildAccountListPayload();
		KycService.getKycCompanyListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data.responseData || {};
					const accountList = Array.isArray(responseData)
						? responseData
						: responseData.kycCompanyLists ||
						responseData.kycGroupCompanyLists ||
						responseData.companyListing ||
						responseData.companyList ||
						responseData.data ||
						[];
					const totalRecords = Number(responseData.totalRecords || accountList.length || 0);
					const currentPage = Number(responseData.currentPage || accountTable.currentPage);

					setAccountTable((prev) => ({
						...prev,
						rows: accountList,
						totalRecords,
						currentPage,
					}));
				} else {
					NotificationManager.error(response.data?.message || "Unable to load Account listing.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load Account listing.");
			})
			.finally(() => {
				setIsAccountLoading(false);
			});
	}

	function handleGetMaterialsList() {
		setIsMaterialsLoading(true);
		const requestData = buildMaterialListPayload();
		KycService.getKycMaterialMasterListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data.responseData || {};
					const materialList = Array.isArray(responseData)
						? responseData
						: responseData.kycMaterialMasterLists ||
						responseData.kycMaterialMasterList ||
						responseData.materialMasterList ||
						responseData.data ||
						[];
					const totalRecords = Number(responseData.totalRecords || materialList.length || 0);
					const rawPage = responseData.currentPage;
					const parsedPage = Number(rawPage);
					const currentPage =
						rawPage !== undefined && rawPage !== null && rawPage !== "" && !Number.isNaN(parsedPage)
							? parsedPage
							: materialsTable.currentPage;

					setMaterialsTable((prev) => ({
						...prev,
						rows: materialList,
						totalRecords,
						currentPage,
					}));
				} else {
					NotificationManager.error(response.data?.message || "Unable to load Materials listing.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load Materials listing.");
			})
			.finally(() => {
				setIsMaterialsLoading(false);
			});
	}

	function handleGetDesignList() {
		setIsDesignLoading(true);
		const requestData = buildDesignListPayload();
		KycService.getKycDesignMasterListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data.responseData || {};
					const designList = Array.isArray(responseData)
						? responseData
						: responseData.desingMasterLists ||
						responseData.kycDesignMasterLists ||
						responseData.designMasterList ||
						responseData.data ||
						[];
					const totalRecords = Number(responseData.totalRecords || designList.length || 0);
					const rawPage = responseData.currentPage;
					const parsedPage = Number(rawPage);
					const currentPage =
						rawPage !== undefined && rawPage !== null && rawPage !== "" && !Number.isNaN(parsedPage)
							? parsedPage
							: designTable.currentPage;

					setDesignTable((prev) => ({
						...prev,
						rows: designList,
						totalRecords,
						currentPage,
					}));
				} else {
					NotificationManager.error(response.data?.message || "Unable to load Design listing.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load Design listing.");
			})
			.finally(() => {
				setIsDesignLoading(false);
			});
	}

	function handleGetQuotationList() {
		setIsQuotationLoading(true);
		const requestData = buildQuotationListPayload();
		KycService.getKycQuotationMasterListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data.responseData || {};
					const quotationList = Array.isArray(responseData)
						? responseData
						: responseData.quotationMasterLists ||
						responseData.quotationMasterList ||
						responseData.kycQuotationMasterLists ||
						responseData.quotationList ||
						responseData.data ||
						[];
					const totalRecords = Number(responseData.totalRecords || quotationList.length || 0);
					const rawPage = responseData.currentPage;
					const parsedPage = Number(rawPage);
					const currentPage =
						rawPage !== undefined && rawPage !== null && rawPage !== "" && !Number.isNaN(parsedPage)
							? parsedPage
							: quotationTable.currentPage;

					setQuotationTable((prev) => ({
						...prev,
						rows: quotationList,
						totalRecords,
						currentPage,
					}));
				} else {
					NotificationManager.error(response.data?.message || "Unable to load Quotation listing.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load Quotation listing.");
			})
			.finally(() => {
				setIsQuotationLoading(false);
			});
	}

	function handleGetOrdersList() {
		setIsOrdersLoading(true);
		const requestData = buildOrdersListPayload();
		KycService.getKycOrderMasterListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data.responseData || {};
					const orderList = Array.isArray(responseData)
						? responseData
						: responseData.orderMasterLists ||
						responseData.orderMasterList ||
						responseData.kycOrderMasterLists ||
						responseData.orderList ||
						responseData.data ||
						[];
					const totalRecords = Number(responseData.totalRecords || orderList.length || 0);
					const rawPage = responseData.currentPage;
					const parsedPage = Number(rawPage);
					const currentPage =
						rawPage !== undefined && rawPage !== null && rawPage !== "" && !Number.isNaN(parsedPage)
							? parsedPage
							: ordersTable.currentPage;

					setOrdersTable((prev) => ({
						...prev,
						rows: orderList,
						totalRecords,
						currentPage,
					}));
				} else {
					NotificationManager.error(response.data?.message || "Unable to load Orders listing.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load Orders listing.");
			})
			.finally(() => {
				setIsOrdersLoading(false);
			});
	}

	const handleTableSearchChange = (event) => {
		const value = event.target.value;
		setTableSearchValue(value);
		if (activeCompanyTab === "account") {
			setAccountTable((prev) => ({ ...prev, currentPage: 1 }));
			return;
		}
		if (activeCompanyTab === "materials") {
			setMaterialsTable((prev) => ({ ...prev, currentPage: 1 }));
			return;
		}
		if (activeCompanyTab === "quotation") {
			setQuotationTable((prev) => ({ ...prev, currentPage: 1 }));
			return;
		}
		if (activeCompanyTab === "orders") {
			setOrdersTable((prev) => ({ ...prev, currentPage: 1 }));
			return;
		}
		if (activeCompanyTab === "design") {
			setDesignTable((prev) => ({ ...prev, currentPage: 1 }));
			return;
		}
		setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1 }));
	};

	const handleGroupCompanyTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setGroupCompanyTable((prev) => ({
			...prev,
			currentPage,
			pageSize: pageSize || prev.pageSize,
		}));
		setGroupCompanySorter((prev) => ({
			field: sortColumn ?? prev.field,
			order: orderBy ?? prev.order,
		}));
	};

	const handleAccountTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setAccountTable((prev) => ({
			...prev,
			currentPage,
			pageSize: pageSize || prev.pageSize,
		}));
		setAccountSorter((prev) => ({
			field: sortColumn ?? prev.field,
			order: orderBy ?? prev.order,
		}));
	};

	const handleMaterialsTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setMaterialsTable((prev) => ({
			...prev,
			currentPage,
			pageSize: pageSize || prev.pageSize,
		}));
		setMaterialsSorter((prev) => ({
			field: sortColumn ?? prev.field,
			order: orderBy ?? prev.order,
		}));
	};

	const handleDesignTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setDesignTable((prev) => ({
			...prev,
			currentPage,
			pageSize: pageSize || prev.pageSize,
		}));
		setDesignSorter((prev) => ({
			field: sortColumn ?? prev.field,
			order: orderBy ?? prev.order,
		}));
	};

	const handleQuotationTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setQuotationTable((prev) => ({
			...prev,
			currentPage,
			pageSize: pageSize || prev.pageSize,
		}));
		setQuotationSorter((prev) => ({
			field: sortColumn ?? prev.field,
			order: orderBy ?? prev.order,
		}));
	};

	const handleOrdersTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setOrdersTable((prev) => ({
			...prev,
			currentPage,
			pageSize: pageSize || prev.pageSize,
		}));
		setOrdersSorter((prev) => ({
			field: sortColumn ?? prev.field,
			order: orderBy ?? prev.order,
		}));
	};

	const handleFilterValueChange = (field, value) => {
		setFilterValues((prev) => ({ ...prev, [field]: value ?? [] }));
		setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1 }));
		setAccountTable((prev) => ({ ...prev, currentPage: 1 }));
		setMaterialsTable((prev) => ({ ...prev, currentPage: 1 }));
		setQuotationTable((prev) => ({ ...prev, currentPage: 1 }));
		setOrdersTable((prev) => ({ ...prev, currentPage: 1 }));
		setDesignTable((prev) => ({ ...prev, currentPage: 1 }));
	};

	const handleResetFilters = () => {
		setFilterValues(defaultFilterValues);
		setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1 }));
		setAccountTable((prev) => ({ ...prev, currentPage: 1 }));
		setMaterialsTable((prev) => ({ ...prev, currentPage: 1 }));
		setQuotationTable((prev) => ({ ...prev, currentPage: 1 }));
		setOrdersTable((prev) => ({ ...prev, currentPage: 1 }));
		setDesignTable((prev) => ({ ...prev, currentPage: 1 }));
		setMaterialsIsplCustomer("");
		setSelectedAccountName("");
		setSelectedGroupCompanyId("");
		setSelectedGroupCompanyName("");
		setTableSearchValue("");
	};

	const persistCompanyNavTabToUrl = (key) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (key === "groupCompany") {
					next.delete("companyTab");
				} else {
					next.set("companyTab", key);
				}
				return next;
			},
			{ replace: true }
		);
	};

	const handleViewModeChange = (mode) => {
		const targetPath = mode === "map" ? "/kyc/map" : "/kyc";
		const currentSearch = searchParams.toString();

		setKycViewMode(mode);
		navigate(
			{
				pathname: targetPath,
				search: currentSearch ? `?${currentSearch}` : "",
			},
			{ replace: true }
		);
	};

	const handleOpenMaterialsForAccountCustomer = (record) => {
		const name = getAccountRowCompanyName(record);
		if (!name) {
			NotificationManager.warning("Company name is not available for this account.");
			return;
		}
		setMaterialsIsplCustomer(name);
		setSelectedAccountName(name);
		// Keep API filter by selected customer, but do not force-fill search input.
		setTableSearchValue("");
		setMaterialsTable((prev) => ({ ...prev, currentPage: 1 }));
		setActiveCompanyTab("materials");
		persistCompanyNavTabToUrl("materials");
	};

	const handleCompanyTabChange = (key) => {
		if (activeCompanyTab === "materials" && key !== "materials") {
			setMaterialsIsplCustomer("");
			if (key !== "account") {
				setSelectedAccountName("");
			}
			setTableSearchValue("");
		}

		if (activeCompanyTab === "groupCompany" && key !== "groupCompany") {
			setSelectedGroupCompanyId("");
			setSelectedGroupCompanyName("");
			setTableSearchValue("");
		}

		if (activeCompanyTab === "account" && key !== "account") {
			setSelectedGroupCompanyId("");
			setSelectedGroupCompanyName("");
			setTableSearchValue("");
		}

		if (activeCompanyTab === "quotation" && key !== "quotation") {
			setTableSearchValue("");
		}

		if (activeCompanyTab === "orders" && key !== "orders") {
			setTableSearchValue("");
		}

		if (activeCompanyTab === "design" && key !== "design") {
			setTableSearchValue("");
		}

		setActiveCompanyTab(key);
		persistCompanyNavTabToUrl(key);

		if (key === "groupCompany") {
			setSelectedGroupCompanyId("");
			setSelectedGroupCompanyName("");
			setTableSearchValue("");
			setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		if (key === "account") {
			setAccountTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		if (key === "materials") {
			setMaterialsTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		if (key === "quotation") {
			setQuotationTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		if (key === "orders") {
			setOrdersTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		if (key === "design") {
			setDesignTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
			return;
		}

		setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1 }));
		setAccountTable((prev) => ({ ...prev, currentPage: 1 }));
		setMaterialsTable((prev) => ({ ...prev, currentPage: 1 }));
		setQuotationTable((prev) => ({ ...prev, currentPage: 1 }));
		setOrdersTable((prev) => ({ ...prev, currentPage: 1 }));
		setDesignTable((prev) => ({ ...prev, currentPage: 1 }));
	};

	const handleOpenAccountTab = (record) => {
		const nextGroupCompanyId = getGroupCompanyId(record);
		if (!nextGroupCompanyId) {
			return;
		}

		setMaterialsIsplCustomer("");
		setSelectedAccountName("");
		setSelectedGroupCompanyId(String(nextGroupCompanyId));
		setSelectedGroupCompanyName(getGroupCompanyRowLabel(record) || getGroupCompanyName(record));
		setAccountTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
		setActiveCompanyTab("account");
		persistCompanyNavTabToUrl("account");
	};

	function handleGetKycDashboardCount() {
		setIsCardsLoading(true);
		KycService.getKycTileCountsAPI(buildKycTileCountsPayload())
			.then((response) => {
				if (response.data?.statusCode === 200) {
					const apiData = response.data.responseData || {};

					setGroupCompanyCount(apiData.groupCompany ? apiData.groupCompany : {});
					setAccountNameCount(apiData.company ? apiData.company : {});
					setContactsCount(apiData.contacts ? apiData.contacts : {});
					setMaterialsCount(getMaterialTileCounts(apiData));
					setDesignsCount(getDesignTileCounts(apiData));
					setCylindersCount(getCylinderTileCounts(apiData));

					setIsCardsLoading(false);
				} else {
					NotificationManager.error(response.data.message);
					setIsCardsLoading(false);
				}
			})
			.catch((error) => {
				setIsCardsLoading(false);
				console.log(error, "Error");
			});
	}

	const filterFieldConfigs = [
		{ key: "distributionChannel", placeholder: "Dist. Channel" },
		{ key: "priceGroup", placeholder: "Price Group" },
		{ key: "salesDistrict", placeholder: "Sales District" },
		{ key: "salesOffice", placeholder: "Sales Office" },
		{ key: "country", placeholder: "Country" },
		{ key: "director", placeholder: "Director" },
		{ key: "generalManager", placeholder: "General Manager" },
		{ key: "continentalManager", placeholder: "Continental Manager" },
		{ key: "regionalManager", placeholder: "Regional Manager" },
		{ key: "areaManager", placeholder: "Area Manager" },
		{ key: "customerGroup", placeholder: "Customer Group" },
		{ key: "materialGroup", placeholder: "Material Group" },
		{ key: "materialType", placeholder: "Material Type" },
		{ key: "materialDivision", placeholder: "Material Division Name" },
		{ key: "materialStructure", placeholder: "Material Structure Name" },
		{ key: "materialCharacteristics", placeholder: "Material Characteristics" },
		{ key: "materialHierarchy1", placeholder: "Material Hierarchy - 1" },
		{ key: "materialHierarchy2", placeholder: "Material Hierarchy - 2" },
		{ key: "materialHierarchy3", placeholder: "Material Hierarchy - 3" },
	];

	const activeTabLabel =
		kycViewMode === "map"
			? "Map"
			: KYC_COMPANY_TAB_LABELS[activeCompanyTab] || "Company Navigation";

	const kycBreadcrumbSegments = useMemo(() => {
		const segments = [];
		const search = tableSearchValue.trim();
		const groupName = selectedGroupCompanyName.trim();
		const materialsCustomer = materialsIsplCustomer.trim();
		const accountName = selectedAccountName.trim();

		if (groupName) {
			segments.push({
				key: `group-${groupName}`,
				label: groupName,
				to: buildKycTableSearchUrl(groupName),
				onNavigate: () => {
					setActiveCompanyTab("groupCompany");
					persistCompanyNavTabToUrl("groupCompany");
					setSelectedGroupCompanyId("");
					setSelectedGroupCompanyName(groupName);
					setMaterialsIsplCustomer("");
					setSelectedAccountName("");
					setTableSearchValue(groupName);
					setGroupCompanyTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
				},
			});
		}

		if (
			accountName &&
			(accountName.toLowerCase() !== groupName.toLowerCase() || !groupName) &&
			(activeCompanyTab === "materials" || activeCompanyTab === "account")
		) {
			segments.push({
				key: `account-${accountName}`,
				label: accountName,
				to: buildKycDashboardReturnLink({
					returnTab: "account",
					groupCompanyId: selectedGroupCompanyId,
					groupCompanyName: groupName,
					accountName,
				}),
				onNavigate: () => {
					setActiveCompanyTab("account");
					persistCompanyNavTabToUrl("account");
					setMaterialsIsplCustomer("");
					setTableSearchValue(accountName);
					setAccountTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
				},
			});
		}

		if (
			search &&
			(!groupName || search.toLowerCase() !== groupName.toLowerCase()) &&
			(!accountName || search.toLowerCase() !== accountName.toLowerCase())
		) {
			const searchTab =
				activeCompanyTab === "materials"
					? "materials"
					: activeCompanyTab === "account"
						? "account"
						: undefined;
			segments.push({
				key: `search-${search}`,
				label: search,
				to: buildKycTableSearchUrl(search, searchTab ? { companyTab: searchTab } : {}),
				onNavigate:
					searchTab === "materials"
						? () => {
							setActiveCompanyTab("materials");
							persistCompanyNavTabToUrl("materials");
							setTableSearchValue(search);
							setMaterialsTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
						}
						: searchTab === "account"
							? () => {
								setActiveCompanyTab("account");
								persistCompanyNavTabToUrl("account");
								setTableSearchValue(search);
								setMaterialsIsplCustomer("");
								setAccountTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
							}
							: undefined,
			});
		}

		if (
			activeCompanyTab === "materials" &&
			materialsCustomer &&
			!accountName &&
			materialsCustomer.toLowerCase() !== search.toLowerCase() &&
			materialsCustomer.toLowerCase() !== groupName.toLowerCase()
		) {
			segments.push({
				key: `materials-${materialsCustomer}`,
				label: materialsCustomer,
				to: buildKycDashboardReturnLink({
					returnTab: "materials",
					groupCompanyName: groupName,
					materialsCustomer,
				}),
				onNavigate: () => {
					setActiveCompanyTab("materials");
					persistCompanyNavTabToUrl("materials");
					setMaterialsIsplCustomer(materialsCustomer);
					setTableSearchValue("");
					setMaterialsTable((prev) => ({ ...prev, currentPage: 1, rows: [] }));
				},
			});
		}

		return segments;
	}, [
		activeCompanyTab,
		tableSearchValue,
		selectedGroupCompanyName,
		selectedGroupCompanyId,
		selectedAccountName,
		materialsIsplCustomer,
	]);

	const handleDashboardBreadcrumbClick = (event) => {
		event.preventDefault();
		handleResetFilters();
		setActiveCompanyTab("groupCompany");
		persistCompanyNavTabToUrl("groupCompany");
		navigate("/kyc", { replace: true });
	};

	const renderViewSwitch = () => (
		<div className="kyc-view-switch-wrap">
			<div className="kyc-view-switch" role="group" aria-label="KYC view">
				<button
					type="button"
					className={`kyc-view-switch-btn ${kycViewMode === "list" ? "active" : ""}`}
					onClick={() => handleViewModeChange("list")}>
					<UnorderedListOutlined />
					<span>List</span>
				</button>
				<button
					type="button"
					className={`kyc-view-switch-btn ${kycViewMode === "map" ? "active" : ""}`}
					onClick={() => handleViewModeChange("map")}>
					<EnvironmentOutlined />
					<span>Map</span>
				</button>
			</div>
		</div>
	);

	const renderKycBreadcrumbs = () => (
		<Breadcrumb className="details_breadcrum kyc-dashboard-breadcrumb" separator=">">
			<Breadcrumb.Item>
				<Link to="/kyc" onClick={handleDashboardBreadcrumbClick}>
					Dashboard
				</Link>
			</Breadcrumb.Item>
			{kycBreadcrumbSegments.map((segment) => (
				<Breadcrumb.Item key={segment.key}>
					{segment.onNavigate ? (
						<Link
							to={segment.to}
							onClick={(event) => {
								event.preventDefault();
								segment.onNavigate();
							}}
							onDoubleClick={(event) => {
								event.preventDefault();
							}}>
							<label>{segment.label}</label>
						</Link>
					) : (
						<Link to={segment.to}>
							<label>{segment.label}</label>
						</Link>
					)}
				</Breadcrumb.Item>
			))}
			{!(
				(activeCompanyTab === "groupCompany" && kycBreadcrumbSegments.length > 0) ||
				(activeCompanyTab === "account" && selectedAccountName.trim())
			) ? (
				<Breadcrumb.Item>
					<label>{activeTabLabel}</label>
				</Breadcrumb.Item>
			) : null}
		</Breadcrumb>
	);

	const renderKycTopBar = () => (
		<div className="kyc-dashboard-top-bar details_view_title">
			<div className="kyc-dashboard-top-bar__breadcrumb">
				{kycViewMode === "list" ? renderKycBreadcrumbs() : null}
			</div>
			{renderViewSwitch()}
		</div>
	);

	return (
		<Fragment>
			<div className="dashboard app-content content">
				<div className="content-wrapper">
					<div className="content-body">
						{renderKycTopBar()}
						{kycViewMode === "map" ? (
							<KycMapView />
						) : (
							<Fragment>
								<Spin size="large" spinning={isCardsLoading}>
									<div className="row">
										{/* Card 1 - Groups Company */}
										<div className="col-xl-4 col-lg-6 col-12">
											<div className="card kycbg_green">
												<div className="card-content">
													<div className="kyc-card-body KYCGroupCompany images_bg">
														<div className="kyc-card-header">
															<div className="kyc-card-title">
																<img src={KYCGroupCompanyImage} alt="Img" />
																<span>
																	{getCardTitle(groupCompanyCount, "Groups Company", [
																		"totalCount",
																		"total",
																	])}
																</span>
															</div>
														</div>
														<div className="kyc-card-stats">
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{Object.keys(groupCompanyCount).length > 0
																		? groupCompanyCount.withSAPCodeCount ||
																		groupCompanyCount.withSAPCode
																		: "-"}
																</h5>
																<h6 className="text-white mb-0">With SAP Code</h6>
															</div>
															<div className="kyc-border-right-white border-right-lighten-3"></div>
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{Object.keys(groupCompanyCount).length > 0
																		? groupCompanyCount.withoutSAPCodeCount ||
																		groupCompanyCount.withoutSAPCode
																		: "-"}
																</h5>
																<h6 className="text-white mb-0">Without SAP Code</h6>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Card 2 - Account Name */}
										<div className="col-xl-4 col-lg-6 col-12">
											<div className="card kycbg_yellow">
												<div className="card-content">
													<div className="kyc-card-body KYCAccountName images_bg">
														<div className="kyc-card-header">
															<div className="kyc-card-title">
																<img src={KYCAccountNameImage} alt="Img" />
																<span>
																	{getCardTitle(accountNameCount, "Account Name", [
																		"totalCount",
																		"total",
																	])}
																</span>
															</div>
														</div>
														<div className="kyc-card-stats">
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(accountNameCount, [
																		"ship_To_Party",
																		"shipToParty",
																	])}
																</h5>
																<h6 className="text-white mb-0">Ship to Party</h6>
															</div>
															<div className="kyc-border-right-white border-right-lighten-3"></div>
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(accountNameCount, [
																		"bill_To_Party",
																		"billToParty",
																	])}
																</h5>
																<h6 className="text-white mb-0">Bill to Party</h6>
															</div>
															<div className="kyc-border-right-white border-right-lighten-3"></div>
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(accountNameCount, ["payer"])}
																</h5>
																<h6 className="text-white mb-0">Payer</h6>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Card 3 - Contacts */}
										<div className="col-xl-4 col-lg-6 col-12">
											<div className="card kycbg_red">
												<div className="card-content">
													<div className="kyc-card-body KYCContacts images_bg">
														<div className="kyc-card-header">
															<div className="kyc-card-title">
																<img src={KYCContactsImage} alt="Img" />
																<span>
																	{getCardTitle(contactsCount, "Contacts", [
																		"totalCount",
																		"total",
																	])}
																</span>
															</div>
														</div>
														<div className="kyc-card-stats">
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{Object.keys(contactsCount).length > 0
																		? contactsCount.running
																		: "-"}
																</h5>
																<h6 className="text-white mb-0">Running</h6>
															</div>
															<div className="kyc-border-right-white border-right-lighten-3"></div>
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{Object.keys(contactsCount).length > 0
																		? contactsCount.hide
																		: "-"}
																</h5>
																<h6 className="text-white mb-0">Hide</h6>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Card 4 - Materials */}
										<div className="col-xl-4 col-lg-6 col-12">
											<div className="card kycbg_blue">
												<div className="card-content">
													<div className="kyc-card-body KYCMaterials images_bg">
														<div className="kyc-card-header">
															<div className="kyc-card-title">
																<img src={KYCMaterialsImage} alt="Img" />
																<span>
																	{getCardTitle(materialsCount, "Materials", [
																		"totalCount",
																		"total",
																	])}
																</span>
															</div>
														</div>
														<div className="kyc-card-stats">
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(materialsCount, [
																		"runningCount",
																		"running",
																	])}
																</h5>
																<h6 className="text-white mb-0">Running</h6>
															</div>
															<div className="kyc-border-right-white border-right-lighten-3"></div>
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(materialsCount, [
																		"hiddenCount",
																		"hide",
																	])}
																</h5>
																<h6 className="text-white mb-0">Hide</h6>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Card 5 - Designs */}
										<div className="col-xl-4 col-lg-6 col-12">
											<div className="card kycbg_grey">
												<div className="card-content">
													<div className="kyc-card-body KYCDesign images_bg">
														<div className="kyc-card-header">
															<div className="kyc-card-title">
																<img src={KYCDesignImage} alt="Img" />
																<span>
																	{getCardTitle(designsCount, "Designs", [
																		"totalCount",
																		"total",
																	])}
																</span>
															</div>
														</div>
														<div className="kyc-card-stats">
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(designsCount, [
																		"runningCount",
																		"running",
																	])}
																</h5>
																<h6 className="text-white mb-0">Running</h6>
															</div>
															<div className="kyc-border-right-white border-right-lighten-3"></div>
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(designsCount, [
																		"hiddenCount",
																		"hide",
																	])}
																</h5>
																<h6 className="text-white mb-0">Hide</h6>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Card 6 - Cylinders */}
										<div className="col-xl-4 col-lg-6 col-12">
											<div className="card kycbg_purple">
												<div className="card-content">
													<div className="kyc-card-body KYCCylinders images_bg">
														<div className="kyc-card-header">
															<div className="kyc-card-title">
																<img src={KYCCylindersImage} alt="Img" />
																<span>
																	{getCardTitle(cylindersCount, "Cylinders", [
																		"totalCount",
																		"total",
																	])}
																</span>
															</div>
														</div>
														<div className="kyc-card-stats">
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(cylindersCount, [
																		"runningCount",
																		"running",
																	])}
																</h5>
																<h6 className="text-white mb-0">Running</h6>
															</div>
															<div className="kyc-border-right-white border-right-lighten-3"></div>
															<div className="card_counts text-left">
																<h5 className="text-white mb-2">
																	{getCardCountValue(cylindersCount, [
																		"hiddenCount",
																		"hide",
																		"close",
																	])}
																</h5>
																<h6 className="text-white mb-0">Close</h6>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</Spin>
								<Card className="kyc-filter-toggle-card" bodyStyle={{ padding: "0" }}>
									<button
										type="button"
										className={`kyc-filter-toggle-btn ${isFiltersExpanded ? "expanded" : "collapsed"}`}
										aria-expanded={isFiltersExpanded}
										onClick={() => setIsFiltersExpanded((prev) => !prev)}>
										<p>Global Filters</p>
										{isFiltersExpanded ? (
											<MinusOutlined className="kyc-filter-toggle-icon" />
										) : (
											<PlusOutlined className="kyc-filter-toggle-icon" />
										)}
									</button>
								</Card>
								<div
									className={`kyc-filter-panel-wrapper ${isFiltersExpanded ? "expanded" : "collapsed"}`}
									style={{ maxHeight: isFiltersExpanded ? `${filterPanelHeight}px` : "0px" }}>
									<div ref={filterPanelContentRef}>
										<Card className="kyc-filter-panel">
											<div className="kyc-filter-panel-header">
												<h5>Global Filters</h5>
												<Button
													type="link"
													className="kyc-filter-reset"
													onClick={handleResetFilters}>
													<span
														className="reset_filter_spn"
													>
														Reset filter
														<img src={imgFilter} alt="img" className="pb_5" />
													</span>
												</Button>
											</div>
											<Spin spinning={isFiltersLoading}>
												<div className="row drawer-content filter_dropdown drawer-select">
													<div className="col-xl-3 col-lg-4 col-md-6 col-12 mb-20 col-xxl-5-custom">
														<DatePicker
															placeholder="From Date"
															value={filterValues.fromDate}
															format="DD-MM-YYYY"
															onChange={(value) => handleFilterValueChange("fromDate", value)}
														/>
													</div>
													<div className="col-xl-3 col-lg-4 col-md-6 col-12 mb-20 col-xxl-5-custom">
														<DatePicker
															placeholder="To Date"
															value={filterValues.toDate}
															format="DD-MM-YYYY"
															onChange={(value) => handleFilterValueChange("toDate", value)}
														/>
													</div>
													{filterFieldConfigs.map((field) => (
														<div className="col-xl-3 col-lg-4 col-md-6 col-12 mb-20 col-xxl-5-custom" key={field.key}>
															<Select
																mode="multiple"
																allowClear
																showSearch
																optionFilterProp="children"
																placeholder={field.placeholder}
																value={filterValues[field.key]}
																onChange={(value) => handleFilterValueChange(field.key, value)}>
																{filterOptions[field.key].map((option) => (
																	<Option key={option.key} value={option.value}>
																		{option.label}
																	</Option>
																))}
															</Select>
														</div>
													))}
												</div>
											</Spin>
										</Card>
									</div>
								</div>
								<div className="row dashboard_tabs material_tabs">
									<div className="col-12">
										<Card className="kyc-table-card">
											<div className="kyc-table-header">
												<h5 className="kyc-company-title">Company Navigation</h5>
												<div className="tab_searchtxt">
													<Search
														placeholder="Search..."
														className="dash_search"
														allowClear
														size="large"
														value={tableSearchValue}
														onChange={handleTableSearchChange}
														disabled={
															activeCompanyTab !== "groupCompany" &&
															activeCompanyTab !== "account" &&
															activeCompanyTab !== "materials" &&
															activeCompanyTab !== "quotation" &&
															activeCompanyTab !== "orders" &&
															activeCompanyTab !== "design"
														}
													/>
												</div>
											</div>
											<Tabs
												activeKey={activeCompanyTab}
												className="dash_tab_panel kyc_company_tabs"
												type="card"
												onChange={handleCompanyTabChange}>
												<TabPane tab="Group Company" key="groupCompany">
													<KycGroupCompanyTab
														tabTableData={groupCompanyTable}
														isLoading={isGroupCompanyLoading}
														tableSorter={groupCompanySorter}
														OnhandleTableSortColumn={handleGroupCompanyTableChange}
														selectedGroupCompanyId={selectedGroupCompanyId}
														onOpenAccountTab={handleOpenAccountTab}
													/>
												</TabPane>
												<TabPane tab="Account" key="account">
													<KycAccountTab
														navigationContext={{
															groupCompanyId: selectedGroupCompanyId,
															groupCompanyName: selectedGroupCompanyName,
															materialsIsplCustomer,
															activeCompanyTab,
														}}
														tabTableData={accountTable}
														isLoading={isAccountLoading}
														tableSorter={accountSorter}
														OnhandleTableSortColumn={handleAccountTableChange}
														onOpenMaterialsByCustomer={handleOpenMaterialsForAccountCustomer}
													/>
												</TabPane>
												<TabPane tab="Materials" key="materials">
													<KycMaterialsTab
														navigationContext={{
															groupCompanyId: selectedGroupCompanyId,
															groupCompanyName: selectedGroupCompanyName,
															materialsIsplCustomer,
															selectedAccountName,
															activeCompanyTab,
														}}
														tabTableData={materialsTable}
														isLoading={isMaterialsLoading}
														tableSorter={materialsSorter}
														OnhandleTableSortColumn={handleMaterialsTableChange}
													/>
												</TabPane>
												<TabPane tab="Quotation" key="quotation">
													<KycQuotationTab
														navigationContext={{
															groupCompanyId: selectedGroupCompanyId,
															groupCompanyName: selectedGroupCompanyName,
														}}
														tabTableData={quotationTable}
														isLoading={isQuotationLoading}
														tableSorter={quotationSorter}
														OnhandleTableSortColumn={handleQuotationTableChange}
													/>
												</TabPane>
												<TabPane tab="Orders" key="orders">
													<KycOrdersTab
														tabTableData={ordersTable}
														isLoading={isOrdersLoading}
														tableSorter={ordersSorter}
														OnhandleTableSortColumn={handleOrdersTableChange}
													/>
												</TabPane>
												<TabPane tab="Design" key="design">
													<KycDesignTab
														navigationContext={{
															groupCompanyId: selectedGroupCompanyId,
															groupCompanyName: selectedGroupCompanyName,
														}}
														tabTableData={designTable}
														isLoading={isDesignLoading}
														tableSorter={designSorter}
														OnhandleTableSortColumn={handleDesignTableChange}
													/>
												</TabPane>
											</Tabs>
										</Card>
									</div>
								</div>
							</Fragment>
						)}
					</div>
				</div>
			</div>
		</Fragment>
	);
};

const mapCenter = { lat: 22.9734, lng: 78.6569 };
const mapZoom = 5;
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const googleMapsLibraries = ["places"];
const googleMapOptions = {
	disableDefaultUI: true,
	zoomControl: false,
	mapTypeControl: false,
	streetViewControl: false,
	fullscreenControl: false,
	clickableIcons: false,
	keyboardShortcuts: false,
};

const markerIconConfig = {
	group: { url: redTickIcon, width: 35, height: 36, tipY: 30.5 },
	company: { url: blueTickIcon, width: 35, height: 36, tipY: 30.5 },
	selected: { url: blackTickIcon, width: 46, height: 53, tipY: 45.5 },
};

const getGoogleMarkerIcon = (type) => {
	const config = markerIconConfig[type] || markerIconConfig.company;
	if (!window.google?.maps) {
		return { url: config.url };
	}

	return {
		url: config.url,
		scaledSize: new window.google.maps.Size(config.width, config.height),
		anchor: new window.google.maps.Point(config.width / 2, config.tipY),
	};
};

const getMapResponseList = (response) => {
	const payload = response?.data;
	const success = payload?.statusCode === 200 || payload?.status === true;
	if (!success) {
		return [];
	}

	const responseData = payload?.responseData ?? payload;
	if (Array.isArray(responseData)) {
		return responseData;
	}

	if (responseData && typeof responseData === "object") {
		const nestedList =
			responseData.companyLocationLists ||
			responseData.companyLocations ||
			responseData.companyLocationList ||
			responseData.locations ||
			responseData.list ||
			responseData.data;
		return Array.isArray(nestedList) ? nestedList : [];
	}

	return [];
};

const toMapCoordinate = (value) => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : null;
};

const toMapLatLng = (latitude, longitude) => {
	if (Math.abs(latitude) > 90 && Math.abs(longitude) <= 90) {
		return { lat: longitude, lng: latitude };
	}
	if (Math.abs(latitude) > 40 && Math.abs(longitude) <= 40) {
		return { lat: longitude, lng: latitude };
	}
	return { lat: latitude, lng: longitude };
};

const buildMapMarkerAddress = (item = {}) => {
	const lineAddress = [item?.address1_Line1, item?.address1_Line2]
		.filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
		.join(", ");
	const regionAddress = [item?.ispl_City, item?.ispl_State, item?.ispl_Country]
		.filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
		.join(", ");

	return (
		getFirstAvailableValue(
			lineAddress,
			item?.formattedAddress,
			item?.companyAddress,
			item?.fullAddress,
			item?.address,
			regionAddress,
			item?.location,
			""
		) || ""
	);
};

const toMapMarkers = (items = []) =>
	items
		.map((item, index) => {
			const latitude = toMapCoordinate(item?.latitude);
			const longitude = toMapCoordinate(item?.longitude);
			if (latitude == null || longitude == null) {
				return null;
			}

			const isGroup =
				String(item?.type || "") === "GroupCompany" ||
				String(item?.type || "").toLowerCase().includes("group");
			const markerId = isGroup
				? `group-${item.groupCompanyId || index}`
				: `company-${item.companyId || index}`;
			const title =
				getFirstAvailableValue(
					item?.companyName,
					item?.groupCompanyName,
					item?.accountName,
					item?.name,
					item?.location,
					"Location"
				) || "Location";
			const phone =
				getFirstAvailableValue(
					item?.mobilePhone,
					item?.mobileNo,
					item?.mobileNumber,
					item?.mobile,
					item?.contactNumber,
					item?.contactNo,
					item?.phoneNumber,
					item?.phone,
					""
				) || "";
			const email =
				getFirstAvailableValue(
					item?.email,
					item?.emailId,
					item?.emailAddress,
					item?.companyEmail,
					item?.primaryEmail,
					""
				) || "";

			return {
				id: markerId,
				type: isGroup ? "group" : "company",
				companyId: item?.companyId ?? item?.company_Id ?? item?.cG_Id ?? "",
				groupCompanyId:
					item?.groupCompanyId ??
					item?.groupCompany_Id ??
					item?.customer_Organisation ??
					"",
				position: toMapLatLng(latitude, longitude),
				title: String(title),
				address: String(buildMapMarkerAddress(item)),
				phone: String(phone),
				email: String(email),
				primaryContactName: String(
					getFirstAvailableValue(item?.primaryContactName, item?.contactName, "") || ""
				),
				raw: item,
			};
		})
		.filter(Boolean);

const getYearlyInvoiceSummaryData = (response) => {
	const payload = response?.data;
	const success = payload?.statusCode === 200 || payload?.status === true;
	if (!success) return null;

	const responseData = payload?.responseData ?? payload?.data ?? payload;
	if (!responseData || typeof responseData !== "object") return null;
	if (Array.isArray(responseData)) return responseData[0] || null;
	return responseData;
};

const formatSummaryNumber = (value, { decimals = 0, suffix = "" } = {}) => {
	if (value === undefined || value === null || value === "") return "—";
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return String(value);

	const formatted = numeric.toLocaleString("en-IN", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
	return suffix ? `${formatted} ${suffix}` : formatted;
};

const buildContactAddress = (contact) => {
	if (!contact || typeof contact !== "object") return "";

	return [contact.address1_Line1, contact.address1_Line2, contact.ispl_City, contact.ispl_State, contact.ispl_Country]
		.filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
		.join(", ");
};

const buildTotalSummaryRows = (summary = {}) => [
	{ label: "Invoice:", value: formatSummaryNumber(summary.invoice_Count) },
	{ label: "Material:", value: formatSummaryNumber(summary.material_Count) },
	{
		label: "Total Num of Bags:",
		value: formatSummaryNumber(summary.total_Dispatch_Qty, { decimals: 2 }),
	},
	{
		label: "Basic Value:",
		value: formatSummaryNumber(summary.total_Net_Amount, { decimals: 2 }),
	},
	{
		label: "Total wt of Bag:",
		value: formatSummaryNumber(summary.total_weight_bag, { decimals: 2, suffix: "MT" }),
	},
	{
		label: "Avg wt per Bag:",
		value: formatSummaryNumber(summary.avg_weight_bag, { decimals: 3, suffix: "KG" }),
	},
	{
		label: "Avg rate per Bag:",
		value: formatSummaryNumber(summary.avgRatePerPic, { decimals: 2 }),
	},
	{
		label: "Avg rate per KG:",
		value: formatSummaryNumber(summary.avgRatePerKg, { decimals: 2 }),
	},
	{ label: "Cylinders:", value: formatSummaryNumber(summary.cylinder) },
	{
		label: "Material Group:",
		value: formatSummaryNumber(summary.material_Group_Count ?? summary.material_group_Count),
	},
];

const toMapSelectOptions = (items = []) =>
	items
		.map((item) => {
			const label =
				item?.name ||
				item?.formattedAddress ||
				item?.address ||
				item?.location ||
				item?.districtCity ||
				item?.state ||
				item?.country;
			const value = item?.id ?? item?.value ?? item?.code ?? label;

			return value != null && label
				? { value: String(value), label: String(label) }
				: null;
		})
		.filter(Boolean);

const serializeMapFilterValue = (value) =>
	(Array.isArray(value) ? value : value ? [value] : [])
		.filter((item) => item !== undefined && item !== null && item !== "")
		.map(String)
		.join(",");

const buildMapFilterPayload = ({
	id = "",
	groupCompanyId = "",
	companyId = "",
	countryId = "",
	stateId = "",
	districtCityId = "",
	locationId = "",
} = {}) => ({
	id: serializeMapFilterValue(id),
	groupCompanyIds: serializeMapFilterValue(groupCompanyId),
	companyIds: serializeMapFilterValue(companyId),
	countries: serializeMapFilterValue(countryId),
	states: serializeMapFilterValue(stateId),
	districtCities: serializeMapFilterValue(districtCityId),
	locations: serializeMapFilterValue(locationId),
});

const buildMapCompanyLocationPayload = (filters = {}) => {
	const filterPayload = buildMapFilterPayload(filters);

	return {
		groupCompanyIds: filterPayload.groupCompanyIds,
		companyIds: filterPayload.companyIds,
		countries: filterPayload.countries,
		states: filterPayload.states,
		districtCities: filterPayload.districtCities,
		locations: filterPayload.locations,
	};
};

const KycMapControls = ({ map, isFullscreen, onToggleFullscreen }) => {
	if (!map) return null;

	return (
		<div className="kyc-map-controls">
			<button
				type="button"
				className="kyc-map-controls__btn"
				onClick={() => map.setZoom((map.getZoom() || mapZoom) + 1)}
				aria-label="Zoom in"
				title="Zoom in">
				+
			</button>
			<button
				type="button"
				className="kyc-map-controls__btn"
				onClick={() => map.setZoom((map.getZoom() || mapZoom) - 1)}
				aria-label="Zoom out"
				title="Zoom out">
				−
			</button>
			<button
				type="button"
				className="kyc-map-controls__btn kyc-map-controls__btn--fullscreen"
				onClick={onToggleFullscreen}
				aria-label={isFullscreen ? "Exit full screen" : "View map in full screen"}
				title={isFullscreen ? "Exit full screen" : "Full screen"}>
				{isFullscreen ? (
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
						<path
							d="M5.5 1H1v4.5M10.5 1H15v4.5M5.5 15H1v-4.5M10.5 15H15v-4.5"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				) : (
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
						<path
							d="M1 5.5V1h4.5M15 5.5V1h-4.5M1 10.5V15h4.5M15 10.5V15h-4.5"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				)}
			</button>
		</div>
	);
};

const KycMapView = () => {
	const mapPanelRef = useRef(null);
	const selectFromLocationRef = useRef(null);
	const markerInfoCloseTimerRef = useRef(null);
	const isPointerOverMarkerRef = useRef(false);
	const isPointerOverInfoWindowRef = useRef(false);
	const lastPointerPositionRef = useRef({ x: 0, y: 0 });
	const [googleMap, setGoogleMap] = useState(null);
	const [isMapFullscreen, setIsMapFullscreen] = useState(false);
	const [groupCompanies, setGroupCompanies] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [countries, setCountries] = useState([]);
	const [states, setStates] = useState([]);
	const [districtCities, setDistrictCities] = useState([]);
	const [locations, setLocations] = useState([]);
	const [selectedGroupCompanyId, setSelectedGroupCompanyId] = useState([]);
	const [selectedCompanyId, setSelectedCompanyId] = useState([]);
	const [selectedCountryId, setSelectedCountryId] = useState([]);
	const [selectedStateId, setSelectedStateId] = useState([]);
	const [selectedDistrictCityId, setSelectedDistrictCityId] = useState([]);
	const [selectedLocationId, setSelectedLocationId] = useState([]);
	const [isGroupCompaniesLoading, setIsGroupCompaniesLoading] = useState(false);
	const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
	const [isCountriesLoading, setIsCountriesLoading] = useState(false);
	const [isStatesLoading, setIsStatesLoading] = useState(false);
	const [isDistrictCitiesLoading, setIsDistrictCitiesLoading] = useState(false);
	const [isLocationsLoading, setIsLocationsLoading] = useState(false);
	const [mapMarkers, setMapMarkers] = useState([]);
	const [selectedMapMarker, setSelectedMapMarker] = useState(null);
	const [selectedContactMarker, setSelectedContactMarker] = useState(null);
	const [isMapInfoPanelVisible, setIsMapInfoPanelVisible] = useState(false);
	const [accountSummary, setAccountSummary] = useState(null);
	const [isAccountSummaryLoading, setIsAccountSummaryLoading] = useState(false);
	const [isMapLocationsLoading, setIsMapLocationsLoading] = useState(false);
	const [isSelectFromLocationOpen, setIsSelectFromLocationOpen] = useState(false);
	const [fromInputValue, setFromInputValue] = useState("");
	const [toInputValue, setToInputValue] = useState("");
	const [waypointInputValue, setWaypointInputValue] = useState("");
	const [fromPlace, setFromPlace] = useState(null);
	const [toPlace, setToPlace] = useState(null);
	const [waypointPlace, setWaypointPlace] = useState(null);
	const [directionsResult, setDirectionsResult] = useState(null);
	const [isRouteSearching, setIsRouteSearching] = useState(false);
	const { isLoaded: isGoogleMapLoaded, loadError: googleMapLoadError } = useJsApiLoader({
		id: "kyc-google-map",
		googleMapsApiKey,
		libraries: googleMapsLibraries,
		version: "weekly",
	});

	useEffect(() => {
		const onFullscreenChange = () => {
			const panel = mapPanelRef.current;
			setIsMapFullscreen(Boolean(panel && document.fullscreenElement === panel));
		};

		document.addEventListener("fullscreenchange", onFullscreenChange);
		return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
	}, []);

	useEffect(() => {
		if (!isSelectFromLocationOpen) return undefined;

		const handleOutsideInteraction = (event) => {
			if (
				selectFromLocationRef.current &&
				!selectFromLocationRef.current.contains(event.target)
			) {
				setIsSelectFromLocationOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutsideInteraction);
		document.addEventListener("touchstart", handleOutsideInteraction);

		return () => {
			document.removeEventListener("mousedown", handleOutsideInteraction);
			document.removeEventListener("touchstart", handleOutsideInteraction);
		};
	}, [isSelectFromLocationOpen]);

	useEffect(() => {
		return () => {
			if (markerInfoCloseTimerRef.current) {
				clearTimeout(markerInfoCloseTimerRef.current);
			}
		};
	}, []);

	const isPointerOverMapInfoWindow = (clientX, clientY) => {
		if (typeof document.elementsFromPoint !== "function") {
			return false;
		}

		return document
			.elementsFromPoint(clientX, clientY)
			.some(
				(element) =>
					element?.closest?.(
						".gm-style-iw-a, .gm-style-iw, .gm-style-iw-c, .gm-style-iw-d, .gm-style-iw-t, .gm-style-iw-chr, .kyc-map-marker-info"
					) != null
			);
	};

	const keepMapMarkerInfoOpen = () => {
		if (markerInfoCloseTimerRef.current) {
			clearTimeout(markerInfoCloseTimerRef.current);
			markerInfoCloseTimerRef.current = null;
		}
	};

	const closeMapMarkerInfo = () => {
		keepMapMarkerInfoOpen();
		isPointerOverMarkerRef.current = false;
		isPointerOverInfoWindowRef.current = false;
		setSelectedMapMarker(null);
	};

	const scheduleCloseMapMarkerInfo = (delayMs = 180) => {
		if (markerInfoCloseTimerRef.current) {
			clearTimeout(markerInfoCloseTimerRef.current);
		}

		markerInfoCloseTimerRef.current = setTimeout(() => {
			const { x, y } = lastPointerPositionRef.current;
			if (
				isPointerOverMarkerRef.current ||
				isPointerOverInfoWindowRef.current ||
				isPointerOverMapInfoWindow(x, y)
			) {
				markerInfoCloseTimerRef.current = null;
				return;
			}
			closeMapMarkerInfo();
			markerInfoCloseTimerRef.current = null;
		}, delayMs);
	};

	const openMapMarkerInfo = (marker) => {
		keepMapMarkerInfoOpen();
		isPointerOverMarkerRef.current = true;
		setSelectedMapMarker(marker);
	};

	const handleMarkerMouseOut = () => {
		isPointerOverMarkerRef.current = false;
		scheduleCloseMapMarkerInfo();
	};

	const handleInfoWindowMouseEnter = () => {
		isPointerOverInfoWindowRef.current = true;
		keepMapMarkerInfoOpen();
	};

	const handleInfoWindowMouseLeave = () => {
		isPointerOverInfoWindowRef.current = false;
		scheduleCloseMapMarkerInfo(120);
	};

	const fetchYearlyInvoiceSummary = (marker) => {
		const isGroup = marker?.type === "group";
		const companyId = marker?.companyId;
		const groupCompanyId = marker?.groupCompanyId;

		// cG_Id = groupCompanyId, customer_Organisation = companyId.
		// Only one identifier is sent; the other stays empty.
		const payload = isGroup
			? {
					cG_Id:
						groupCompanyId !== undefined &&
						groupCompanyId !== null &&
						String(groupCompanyId).trim() !== "" &&
						String(groupCompanyId) !== "0"
							? String(groupCompanyId)
							: "",
					customer_Organisation: "",
			  }
			: {
					cG_Id: "",
					customer_Organisation:
						companyId !== undefined &&
						companyId !== null &&
						String(companyId).trim() !== "" &&
						String(companyId) !== "0"
							? String(companyId)
							: "",
			  };

		setIsAccountSummaryLoading(true);
		setAccountSummary(null);

		return KycService.getKycMapYearlyInvoiceSummaryAPI(payload)
			.then((response) => {
				const summary = getYearlyInvoiceSummaryData(response);
				setAccountSummary(summary);
				if (!summary) {
					NotificationManager.warning("No account summary found for this location.");
				}
			})
			.catch((error) => {
				setAccountSummary(null);
				NotificationManager.error(
					error?.message || "Unable to load yearly invoice summary."
				);
			})
			.finally(() => {
				setIsAccountSummaryLoading(false);
			});
	};

	const handleMarkerClick = (marker) => {
		openMapMarkerInfo(marker);
		setSelectedContactMarker(marker);
		setIsMapInfoPanelVisible(true);
		fetchYearlyInvoiceSummary(marker);
	};

	useKycMapCompanyCluster({
		map: googleMap,
		markers: mapMarkers,
		selectedMarkerId: selectedContactMarker?.id || null,
		getGoogleMarkerIcon,
		onMarkerClick: handleMarkerClick,
		onMarkerMouseOver: openMapMarkerInfo,
		onMarkerMouseOut: handleMarkerMouseOut,
	});

	useEffect(() => {
		if (!selectedMapMarker) return undefined;

		const handlePointerMove = (event) => {
			lastPointerPositionRef.current = {
				x: event.clientX,
				y: event.clientY,
			};

			if (
				isPointerOverMarkerRef.current ||
				isPointerOverInfoWindowRef.current ||
				isPointerOverMapInfoWindow(event.clientX, event.clientY)
			) {
				keepMapMarkerInfoOpen();
				return;
			}

			scheduleCloseMapMarkerInfo();
		};

		document.addEventListener("mousemove", handlePointerMove);
		return () => document.removeEventListener("mousemove", handlePointerMove);
	}, [selectedMapMarker]);

	// Bind hover close to the full Google InfoWindow chrome (not only React content).
	useEffect(() => {
		if (!selectedMapMarker) {
			isPointerOverInfoWindowRef.current = false;
			return undefined;
		}

		let iwRoot = null;

		const onEnter = () => {
			isPointerOverInfoWindowRef.current = true;
			if (markerInfoCloseTimerRef.current) {
				clearTimeout(markerInfoCloseTimerRef.current);
				markerInfoCloseTimerRef.current = null;
			}
		};

		const onLeave = () => {
			isPointerOverInfoWindowRef.current = false;
			if (markerInfoCloseTimerRef.current) {
				clearTimeout(markerInfoCloseTimerRef.current);
			}
			markerInfoCloseTimerRef.current = setTimeout(() => {
				const { x, y } = lastPointerPositionRef.current;
				if (
					isPointerOverMarkerRef.current ||
					isPointerOverInfoWindowRef.current ||
					isPointerOverMapInfoWindow(x, y)
				) {
					markerInfoCloseTimerRef.current = null;
					return;
				}
				isPointerOverMarkerRef.current = false;
				isPointerOverInfoWindowRef.current = false;
				setSelectedMapMarker(null);
				markerInfoCloseTimerRef.current = null;
			}, 120);
		};

		const bindTimer = window.setTimeout(() => {
			iwRoot =
				document.querySelector(".gm-style-iw-a") ||
				document.querySelector(".gm-style-iw-c");
			if (!iwRoot) return;
			iwRoot.addEventListener("mouseenter", onEnter);
			iwRoot.addEventListener("mouseleave", onLeave);
		}, 0);

		return () => {
			window.clearTimeout(bindTimer);
			if (iwRoot) {
				iwRoot.removeEventListener("mouseenter", onEnter);
				iwRoot.removeEventListener("mouseleave", onLeave);
			}
			isPointerOverInfoWindowRef.current = false;
		};
	}, [selectedMapMarker]);

	useEffect(() => {
		if (!googleMap) return;

		window.google?.maps?.event?.trigger(googleMap, "resize");

		if (directionsResult?.routes?.[0]?.bounds) {
			googleMap.fitBounds(directionsResult.routes[0].bounds, 48);
			return;
		}

		if (!mapMarkers.length) {
			googleMap.setCenter(mapCenter);
			googleMap.setZoom(mapZoom);
			return;
		}

		if (mapMarkers.length === 1) {
			googleMap.setCenter(mapMarkers[0].position);
			googleMap.setZoom(8);
			return;
		}

		const bounds = new window.google.maps.LatLngBounds();
		mapMarkers.forEach((marker) => bounds.extend(marker.position));
		googleMap.fitBounds(bounds, 40);
	}, [googleMap, isMapFullscreen, mapMarkers, directionsResult]);

	// Panel open/close only resizes the map canvas — keep current center/zoom.
	useEffect(() => {
		if (!googleMap) return undefined;

		const preserveViewportAfterResize = () => {
			const center = googleMap.getCenter();
			const zoom = googleMap.getZoom();
			window.google?.maps?.event?.trigger(googleMap, "resize");
			if (center) googleMap.setCenter(center);
			if (zoom != null) googleMap.setZoom(zoom);
		};

		preserveViewportAfterResize();
		const resizeTimer = window.setTimeout(preserveViewportAfterResize, 340);
		return () => window.clearTimeout(resizeTimer);
	}, [googleMap, isMapInfoPanelVisible]);

	const fetchMapFilterOptions = (request, setOptions, setLoading, errorMessage) => {
		setLoading(true);
		return request()
			.then((response) => {
				setOptions(toMapSelectOptions(getMapResponseList(response)));
			})
			.catch((error) => {
				setOptions([]);
				NotificationManager.error(error?.message || errorMessage);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchCompanyLocationList = (filters = {}) => {
		const payload = buildMapCompanyLocationPayload(filters);

		setIsMapLocationsLoading(true);
		KycService.getKycMapCompanyLocationListAPI(payload)
			.then((response) => {
				setSelectedMapMarker(null);
				setSelectedContactMarker(null);
				setIsMapInfoPanelVisible(false);
				setAccountSummary(null);
				setMapMarkers(toMapMarkers(getMapResponseList(response)));
			})
			.catch((error) => {
				setSelectedMapMarker(null);
				setSelectedContactMarker(null);
				setIsMapInfoPanelVisible(false);
				setAccountSummary(null);
				setMapMarkers([]);
				NotificationManager.error(error?.message || "Unable to load map locations.");
			})
			.finally(() => {
				setIsMapLocationsLoading(false);
			});
	};

	const fetchRouteCompanyLocations = (locationFilter = {}, directions = null) => {
		const payload = {
			groupCompanyIds: serializeMapFilterValue(selectedGroupCompanyId),
			companyIds: serializeMapFilterValue(selectedCompanyId),
			countries: locationFilter.countries || "",
			states: locationFilter.states || "",
			districtCities: locationFilter.districtCities || "",
			locations: "",
		};

		setIsMapLocationsLoading(true);
		return KycService.getKycMapCompanyLocationListAPI(payload)
			.then((response) => {
				const allMarkers = toMapMarkers(getMapResponseList(response));
				const pathCoordinates = getDirectionsPathCoordinates(directions);
				const stopPoints = [
					extractLocation(fromPlace),
					extractLocation(toPlace),
					waypointPlace ? extractLocation(waypointPlace) : null,
				].filter((stop) => stop?.lat != null && stop?.lng != null);

				const nearbyMarkers = filterMarkersNearRoute(
					allMarkers,
					pathCoordinates,
					stopPoints
				);

				setSelectedMapMarker(null);
				setSelectedContactMarker(null);
				setIsMapInfoPanelVisible(false);
				setAccountSummary(null);
				setMapMarkers(nearbyMarkers);

				if (allMarkers.length && !nearbyMarkers.length) {
					NotificationManager.info(
						"No companies found near this route. Try a different From/To location."
					);
				}
			})
			.catch((error) => {
				setSelectedMapMarker(null);
				setSelectedContactMarker(null);
				setIsMapInfoPanelVisible(false);
				setAccountSummary(null);
				setMapMarkers([]);
				NotificationManager.error(error?.message || "Unable to load map locations.");
			})
			.finally(() => {
				setIsMapLocationsLoading(false);
			});
	};

	useEffect(() => {
		// Independent roots: Group Company, Country, and Select Location.
		fetchMapFilterOptions(
			() => KycService.getKycMapGroupCompanyAPI(buildMapFilterPayload()),
			setGroupCompanies,
			setIsGroupCompaniesLoading,
			"Unable to load group companies."
		);
		fetchMapFilterOptions(
			() => KycService.getKycMapCountryAPI(buildMapFilterPayload()),
			setCountries,
			setIsCountriesLoading,
			"Unable to load countries."
		);
		fetchMapFilterOptions(
			() => KycService.getKycMapFormattedAddressAPI(buildMapFilterPayload()),
			setLocations,
			setIsLocationsLoading,
			"Unable to load locations."
		);
	}, []);

	const hasSelectedMapFilters = [
		selectedGroupCompanyId,
		selectedCompanyId,
		selectedCountryId,
		selectedStateId,
		selectedDistrictCityId,
		selectedLocationId,
	].some((value) => Array.isArray(value) && value.length > 0);

	const handleApplyFilters = () => {
		if (!hasSelectedMapFilters) return;

		setDirectionsResult(null);
		fetchCompanyLocationList({
			groupCompanyId: selectedGroupCompanyId,
			companyId: selectedCompanyId,
			countryId: selectedCountryId,
			stateId: selectedStateId,
			districtCityId: selectedDistrictCityId,
			locationId: selectedLocationId,
		});
	};

	const handleResetMapFilters = () => {
		setSelectedGroupCompanyId([]);
		setSelectedCompanyId([]);
		setSelectedCountryId([]);
		setSelectedStateId([]);
		setSelectedDistrictCityId([]);
		setSelectedLocationId([]);
		setCompanies([]);
		setStates([]);
		setDistrictCities([]);
		setMapMarkers([]);
		setSelectedMapMarker(null);
		setSelectedContactMarker(null);
		setIsMapInfoPanelVisible(false);
		setAccountSummary(null);
		setDirectionsResult(null);
	};

	const handleFromPlaceSelected = (place) => {
		const extracted = extractLocation(place);
		setFromPlace(place);
		setFromInputValue(extracted.label || place.name || fromInputValue);
	};

	const handleToPlaceSelected = (place) => {
		const extracted = extractLocation(place);
		setToPlace(place);
		setToInputValue(extracted.label || place.name || toInputValue);
	};

	const handleWaypointPlaceSelected = (place) => {
		const extracted = extractLocation(place);
		setWaypointPlace(place);
		setWaypointInputValue(extracted.label || place.name || waypointInputValue);
	};

	const handleSwapRouteLocations = () => {
		setFromPlace(toPlace);
		setToPlace(fromPlace);
		setFromInputValue(toInputValue);
		setToInputValue(fromInputValue);
	};

	const handleResetSelectFromLocation = () => {
		setFromInputValue("");
		setToInputValue("");
		setWaypointInputValue("");
		setFromPlace(null);
		setToPlace(null);
		setWaypointPlace(null);
		setDirectionsResult(null);
		setMapMarkers([]);
		setSelectedMapMarker(null);
		setSelectedContactMarker(null);
		setIsMapInfoPanelVisible(false);
		setAccountSummary(null);
	};

	const handleRouteSearch = () => {
		const fromLocation = getPlaceGeometryLocation(fromPlace);
		const toLocation = getPlaceGeometryLocation(toPlace);

		if (!fromLocation || !toLocation) {
			NotificationManager.warning("Please select both From and To locations from suggestions.");
			return;
		}

		if (!window.google?.maps?.DirectionsService) {
			NotificationManager.error("Google Directions is not available yet. Please try again.");
			return;
		}

		const waypointLocation =
			waypointPlace ? getPlaceGeometryLocation(waypointPlace) : null;
		const waypoints = waypointLocation
			? [{ location: waypointLocation, stopover: true }]
			: [];

		setIsRouteSearching(true);
		const directionsService = new window.google.maps.DirectionsService();
		directionsService.route(
			{
				origin: fromLocation,
				destination: toLocation,
				waypoints,
				travelMode: window.google.maps.TravelMode.DRIVING,
				optimizeWaypoints: false,
			},
			(result, status) => {
				if (status !== window.google.maps.DirectionsStatus.OK || !result) {
					setDirectionsResult(null);
					setIsRouteSearching(false);
					NotificationManager.error("Unable to find a driving route for the selected locations.");
					return;
				}

				setDirectionsResult(result);
				const locationFilter = buildLocationFilter(
					fromPlace,
					toPlace,
					waypointPlace ? [waypointPlace] : []
				);
				fetchRouteCompanyLocations(locationFilter, result).finally(() => {
					setIsRouteSearching(false);
				});
			}
		);
	};

	// Chain 1: Group Company → Company
	const handleGroupCompanyChange = (value) => {
		setSelectedGroupCompanyId(value);
		setSelectedCompanyId([]);
		setCompanies([]);

		if (value.length) {
			const payload = buildMapFilterPayload({
				groupCompanyId: value,
			});
			fetchMapFilterOptions(
				() => KycService.getKycMapCompanyAPI(payload),
				setCompanies,
				setIsCompaniesLoading,
				"Unable to load companies."
			);
		}
	};

	const handleCompanyChange = (value) => {
		setSelectedCompanyId(value);
	};

	// Chain 2: Country → State → District/City
	const handleCountryChange = (value) => {
		setSelectedCountryId(value);
		setSelectedStateId([]);
		setSelectedDistrictCityId([]);
		setStates([]);
		setDistrictCities([]);

		if (value.length) {
			const payload = buildMapFilterPayload({
				countryId: value,
			});
			fetchMapFilterOptions(
				() => KycService.getKycMapStateAPI(payload),
				setStates,
				setIsStatesLoading,
				"Unable to load states."
			);
		}
	};

	const handleStateChange = (value) => {
		setSelectedStateId(value);
		setSelectedDistrictCityId([]);
		setDistrictCities([]);

		if (value.length) {
			const payload = buildMapFilterPayload({
				countryId: selectedCountryId,
				stateId: value,
			});
			fetchMapFilterOptions(
				() => KycService.getKycMapDistrictCityAPI(payload),
				setDistrictCities,
				setIsDistrictCitiesLoading,
				"Unable to load districts/cities."
			);
		}
	};

	const handleDistrictCityChange = (value) => {
		setSelectedDistrictCityId(value);
	};

	// Chain 3: Select Location is free / independent (loaded on mount).

	const toggleMapFullscreen = () => {
		const panel = mapPanelRef.current;
		if (!panel) return;

		if (document.fullscreenElement === panel) {
			document.exitFullscreen?.();
			return;
		}

		const request =
			panel.requestFullscreen ||
			panel.webkitRequestFullscreen ||
			panel.msRequestFullscreen;

		request?.call(panel);
	};

	return (
		<Card className="kyc-map-card">
			<div className="kyc-map-filters">
				<Select
					mode="multiple"
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Group Company Name"
					loading={isGroupCompaniesLoading}
					value={selectedGroupCompanyId}
					onChange={handleGroupCompanyChange}
					options={groupCompanies}
				/>
				<Select
					mode="multiple"
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Company Name"
					loading={isCompaniesLoading}
					disabled={!selectedGroupCompanyId.length}
					value={selectedCompanyId}
					onChange={handleCompanyChange}
					options={companies}
				/>
				<Select
					mode="multiple"
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Country"
					loading={isCountriesLoading}
					value={selectedCountryId}
					onChange={handleCountryChange}
					options={countries}
				/>
				<Select
					mode="multiple"
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="State"
					loading={isStatesLoading}
					disabled={!selectedCountryId.length}
					value={selectedStateId}
					onChange={handleStateChange}
					options={states}
				/>
				<Select
					mode="multiple"
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="District/City"
					loading={isDistrictCitiesLoading}
					disabled={!selectedStateId.length}
					value={selectedDistrictCityId}
					onChange={handleDistrictCityChange}
					options={districtCities}
				/>
				<Select
					mode="multiple"
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Select Location"
					loading={isLocationsLoading}
					value={selectedLocationId}
					onChange={setSelectedLocationId}
					options={locations}
				/>
				<Button
					className="kyc-map-apply-btn"
					loading={isMapLocationsLoading}
					disabled={!hasSelectedMapFilters}
					onClick={handleApplyFilters}>
					Apply
				</Button>
				<Button
					className="kyc-map-reset-btn"
					disabled={!hasSelectedMapFilters && !mapMarkers.length}
					onClick={handleResetMapFilters}>
					Reset
				</Button>
			</div>
			<div
				className={`kyc-map-layout${
					isMapInfoPanelVisible ? " has-info-panel" : " is-map-only"
				}`}>
				<div
					ref={mapPanelRef}
					className={`kyc-static-map${isMapFullscreen ? " is-fullscreen" : ""}`}>
					{googleMapLoadError || !googleMapsApiKey ? (
						<div className="kyc-map-missing-key">
							Google Maps could not be loaded. Check REACT_APP_GOOGLE_MAPS_API_KEY.
						</div>
					) : isGoogleMapLoaded ? (
						<GoogleMap
							mapContainerClassName="kyc-google-map"
							center={mapCenter}
							zoom={mapZoom}
							options={{
								...googleMapOptions,
								scrollwheel: isMapFullscreen,
								gestureHandling: isMapFullscreen ? "greedy" : "cooperative",
							}}
							onLoad={setGoogleMap}
							onUnmount={() => setGoogleMap(null)}>
							{directionsResult && (
								<DirectionsRenderer
									directions={directionsResult}
									options={{
										suppressMarkers: true,
										preserveViewport: true,
										polylineOptions: {
											strokeColor: "#1890ff",
											strokeOpacity: 0.9,
											strokeWeight: 5,
										},
									}}
								/>
							)}
							{(() => {
								const fromLoc = extractLocation(fromPlace);
								return fromLoc.lat != null && fromLoc.lng != null ? (
									<Marker
										position={{ lat: fromLoc.lat, lng: fromLoc.lng }}
										title={fromInputValue || "From"}
										label={{ text: "A", color: "#fff", fontWeight: "700" }}
									/>
								) : null;
							})()}
							{(() => {
								const toLoc = extractLocation(toPlace);
								return toLoc.lat != null && toLoc.lng != null ? (
									<Marker
										position={{ lat: toLoc.lat, lng: toLoc.lng }}
										title={toInputValue || "To"}
										label={{ text: "B", color: "#fff", fontWeight: "700" }}
									/>
								) : null;
							})()}
							{(() => {
								// Optional waypoint marker (C) when Add Destination is selected
								if (!waypointPlace) return null;
								const waypointLoc = extractLocation(waypointPlace);
								return waypointLoc.lat != null && waypointLoc.lng != null ? (
									<Marker
										position={{ lat: waypointLoc.lat, lng: waypointLoc.lng }}
										title={waypointInputValue || "Waypoint"}
										label={{ text: "C", color: "#fff", fontWeight: "700" }}
									/>
								) : null;
							})()}
							{selectedMapMarker && (
								<InfoWindow
									position={selectedMapMarker.position}
									options={{
										maxWidth: 320,
										pixelOffset: window.google?.maps
											? new window.google.maps.Size(0, -36)
											: undefined,
									}}
									onCloseClick={closeMapMarkerInfo}>
									<div
										className="kyc-map-marker-info"
										onMouseEnter={handleInfoWindowMouseEnter}
										onMouseLeave={handleInfoWindowMouseLeave}>
										<div className="kyc-map-marker-info__hero">
											<img
												className="kyc-map-marker-info__logo"
												src={knackLogo}
												alt="Knack"
											/>
										</div>
										<div className="kyc-map-marker-info__content">
											<div className="kyc-map-marker-info__title">
												<img
													className="kyc-map-marker-info__icon kyc-map-marker-info__icon--location"
													src={
														selectedMapMarker.type === "group"
															? infoWindowLocationRedIcon
															: infoWindowLocationIcon
													}
													alt=""
													aria-hidden
												/>
												<strong>{selectedMapMarker.title}</strong>
											</div>
											{selectedMapMarker.address ? (
												<p className="kyc-map-marker-info__address">
													{selectedMapMarker.address}
												</p>
											) : null}
											{(selectedMapMarker.phone || selectedMapMarker.email) && (
												<div className="kyc-map-marker-info__contacts">
													{selectedMapMarker.phone ? (
														<a href={`tel:${selectedMapMarker.phone}`}>
															<img
																className="kyc-map-marker-info__icon"
																src={infoWindowPhoneIcon}
																alt=""
																aria-hidden
															/>
															<span>{selectedMapMarker.phone}</span>
														</a>
													) : null}
													{selectedMapMarker.email ? (
														<a href={`mailto:${selectedMapMarker.email}`}>
															<img
																className="kyc-map-marker-info__icon"
																src={infoWindowMailIcon}
																alt=""
																aria-hidden
															/>
															<span>{selectedMapMarker.email}</span>
														</a>
													) : null}
												</div>
											)}
											<a
												className="kyc-map-marker-info__direction"
												href={`https://www.google.com/maps/dir/?api=1&destination=${selectedMapMarker.position.lat},${selectedMapMarker.position.lng}`}
												target="_blank"
												rel="noreferrer">
												Get Direction
												<img
													className="kyc-map-marker-info__direction-icon"
													src={infoWindowChevronIcon}
													alt=""
													aria-hidden
												/>
											</a>
										</div>
									</div>
								</InfoWindow>
							)}
						</GoogleMap>
					) : (
						<div className="kyc-map-missing-key">Loading map...</div>
					)}
					<div ref={selectFromLocationRef} className="kyc-map-select-location">
						<button
							type="button"
							className={`kyc-map-select-location__trigger${isSelectFromLocationOpen ? " is-active" : ""
								}`}
							onClick={() => setIsSelectFromLocationOpen((open) => !open)}
							aria-expanded={isSelectFromLocationOpen}
							aria-controls="kyc-select-from-location-panel">
							Select From Location
							<img src={selectFromLocationIcon} alt="" aria-hidden />
						</button>
						<div
							id="kyc-select-from-location-panel"
							className={`kyc-map-select-location__panel${isSelectFromLocationOpen ? " is-open" : ""
								}`}
							role="dialog"
							aria-label="Select From Location"
							aria-hidden={!isSelectFromLocationOpen}>
							<h5 className="kyc-map-select-location__title">Select From Location</h5>
							<div className="kyc-map-select-location__route">
								<div className="kyc-map-select-location__stops">
									<div className="kyc-map-select-location__stop">
										<img
											className="kyc-map-select-location__location-icon"
											src={locationFromIcon}
											alt=""
											aria-hidden
										/>
										{isGoogleMapLoaded ? (
											<KycMapPlaceInput
												value={fromInputValue}
												placeholder="From location"
												onChange={(value) => {
													setFromInputValue(value);
													if (!value) setFromPlace(null);
												}}
												onPlaceSelected={handleFromPlaceSelected}
											/>
										) : (
											<Input
												className="kyc-map-select-location__input"
												value={fromInputValue}
												placeholder="From location"
												onChange={(event) => setFromInputValue(event.target.value)}
											/>
										)}
									</div>
									<div className="kyc-map-select-location__stop">
										<img
											className="kyc-map-select-location__location-icon"
											src={locationToIcon}
											alt=""
											aria-hidden
										/>
										{isGoogleMapLoaded ? (
											<KycMapPlaceInput
												value={toInputValue}
												placeholder="To location"
												onChange={(value) => {
													setToInputValue(value);
													if (!value) setToPlace(null);
												}}
												onPlaceSelected={handleToPlaceSelected}
											/>
										) : (
											<Input
												className="kyc-map-select-location__input"
												value={toInputValue}
												placeholder="To location"
												onChange={(event) => setToInputValue(event.target.value)}
											/>
										)}
									</div>
									<div className="kyc-map-select-location__stop">
										<img
											className="kyc-map-select-location__location-icon"
											src={addLocationIcon}
											alt=""
											aria-hidden
										/>
										{isGoogleMapLoaded ? (
											<KycMapPlaceInput
												value={waypointInputValue}
												placeholder="Add Destination"
												onChange={(value) => {
													setWaypointInputValue(value);
													if (!value) setWaypointPlace(null);
												}}
												onPlaceSelected={handleWaypointPlaceSelected}
											/>
										) : (
											<Input
												className="kyc-map-select-location__input"
												value={waypointInputValue}
												placeholder="Add Destination"
												onChange={(event) => setWaypointInputValue(event.target.value)}
											/>
										)}
									</div>
								</div>
								<button
									type="button"
									className="kyc-map-select-location__swap"
									aria-label="Swap locations"
									title="Swap locations"
									onClick={handleSwapRouteLocations}>
									<img src={locationSwapIcon} alt="" aria-hidden />
								</button>
							</div>
							<div className="kyc-map-select-location__actions">
								<Button
									className="kyc-map-select-location__search"
									type="primary"
									loading={isRouteSearching || isMapLocationsLoading}
									disabled={
										!getPlaceGeometryLocation(fromPlace) ||
										!getPlaceGeometryLocation(toPlace)
									}
									onClick={handleRouteSearch}>
									Search
								</Button>
								<Button
									className="kyc-map-select-location__reset"
									disabled={
										!fromInputValue &&
										!toInputValue &&
										!waypointInputValue &&
										!directionsResult &&
										!mapMarkers.length
									}
									onClick={handleResetSelectFromLocation}>
									Reset
								</Button>
							</div>
						</div>
					</div>
					<div className="kyc-map-legend">
						<span>
							<img src={redTickIcon} alt="" />
							Group Company
						</span>
						<span>
							<img src={blueTickIcon} alt="" />
							Company
						</span>
					</div>
					<KycMapControls
						map={googleMap}
						isFullscreen={isMapFullscreen}
						onToggleFullscreen={toggleMapFullscreen}
					/>
				</div>
				<aside
					className={`kyc-map-info${isMapInfoPanelVisible ? " is-open" : ""}`}
					aria-hidden={!isMapInfoPanelVisible}>
					<div className="kyc-map-info__inner">
						{(() => {
							const contactInfo = accountSummary?.contactInfo || null;
							const contactName =
								contactInfo?.name ||
								selectedContactMarker?.title ||
								"Company Name";
							const contactAddress =
								buildContactAddress(contactInfo) ||
								selectedContactMarker?.address ||
								"";
							const contactPhone =
								contactInfo?.mobilePhone || selectedContactMarker?.phone || "";
							const contactEmail =
								contactInfo?.email || selectedContactMarker?.email || "";
							const contactPerson =
								contactInfo?.primaryContactName ||
								selectedContactMarker?.primaryContactName ||
								"";
							const totalSummaryRows = buildTotalSummaryRows(
								accountSummary?.summary || {}
							);

							return (
								<>
									<h4>Contact Information</h4>
									<div className="kyc-contact-heading">
										<EnvironmentFilled />
										<strong>{contactName}</strong>
									</div>
									{isAccountSummaryLoading && !contactInfo ? (
										<div className="kyc-account-info-loading">
											<Spin size="small" />
											<span>Loading contact details...</span>
										</div>
									) : (
										<>
											{contactAddress ? (
												<p className="kyc-map-info__address">{contactAddress}</p>
											) : null}
											{contactPhone ? (
												<p className="kyc-map-info__meta">
													Mobile No - {contactPhone}
												</p>
											) : null}
											{contactPerson ? (
												<p className="kyc-map-info__meta">
													Contact - {contactPerson}
												</p>
											) : null}
											<div className="kyc-map-info__note-card">
												{contactEmail || "NA"}
											</div>
											{!contactAddress && !contactPhone && !contactPerson ? (
												<p className="kyc-map-info__meta">
													No contact details available for this location.
												</p>
											) : null}
										</>
									)}

									<h4 className="kyc-map-info__summary-title">Total Summary</h4>
									<div className="kyc-total-summary-card">
										{isAccountSummaryLoading ? (
											<div className="kyc-account-info-loading">
												<Spin size="small" />
												<span>Loading total summary...</span>
											</div>
										) : (
											totalSummaryRows.map((row) => (
												<div className="kyc-total-summary-row" key={row.label}>
													<span>{row.label}</span>
													<strong>{row.value}</strong>
												</div>
											))
										)}
									</div>
								</>
							);
						})()}
					</div>
				</aside>
			</div>
		</Card>
	);
};

export default KycDashboard;
